// Demo mode: the whole app against made-up data held in memory. It mirrors the database's visibility rules closely
// enough for each role to see what they would really see, but it is not a security boundary and never talks to a
// server.
import { type Api, ApiError, type MfaState, PAGE_SIZE, RAW_CALLS_PAGE } from "./api";
import type { ReviewLead } from "./api";
import type { CallUsage, ModelUsage, PlatformAiUsage } from "./aiUsage";
import {
  type AiInstructions,
  asBusinessType,
  type BusinessType,
  type BusinessTypeResult,
  type CallType,
  type ChecklistItem,
  checklistProblem,
  type ChecklistTally,
  type FactField,
  factsProblem,
  MAX_CATEGORIES,
  MAX_NOTE,
  TEXT_LIMITS,
  type TextField,
  textProblem,
} from "./aiInstructions";
import {
  buildDemo,
  DEMO_CATEGORIES,
  DEMO_CHECKLIST,
  DEMO_EDUCATION_CATEGORIES,
  DEMO_EDUCATION_CHECKLIST,
  DEMO_SOFTWARE_CATEGORIES,
  DEMO_SOFTWARE_CHECKLIST,
  DEMO_TZ,
  demoAudioUrl,
  type DemoDb,
} from "./demoData";
import {
  type Bucket,
  type CustomerFollowUp,
  type CustomerList,
  type CustomerPage,
  type CustomerQuery,
  type CustomerRow,
  CUSTOMERS_PAGE,
  type CustomerStatus,
  DETAIL_FIELDS,
  FOLLOW_UP_KINDS,
  FOLLOW_UPS_PAGE,
  type FollowUpInput,
  type FollowUpKind,
  followUpKind,
  type FollowUpList,
  type FollowUpPerson,
  type FollowUpQuery,
  type FollowUpRow,
  type FollowUpType,
  type Insights,
  isVisit,
  type Label,
  messageProblem,
  type MessageTemplate,
  type MessageTemplateInput,
  type Ref,
  type StageRef,
  type TimelineEvent,
  type VisitResult,
} from "./crm";
import { type Disposition, type DispositionCall, type Dispositions, dispositionsProblem } from "./dispositions";
import type { Json } from "./database.types";
import { ACTIVE_STAGES, fromWallTime, localDate, wallParts } from "./format";
import { parseVisitSettings, type VisitPublic } from "./visits";
import { INTEGRATIONS, type UseCase } from "./integrations";
import { recordingDue } from "./recordingDue";
import type {
  AddOns,
  AnalysisOutput,
  BillingSummary,
  Call,
  CallDetail,
  CallNote,
  CompanyHealth,
  CompanyHealthDetail,
  CompanyIntegration,
  CompanyStatus,
  CompanyTeamMember,
  DashboardRange,
  EraseResult,
  FacebookAssign,
  FacebookChosen,
  FacebookLeadRow,
  FacebookPage,
  FacebookPageChoice,
  FacebookState,
  HealthOverview,
  ImportOptions,
  ImportResult,
  ImportRow,
  IndiaMartPull,
  IndiaMartRead,
  IntegrationActivityItem,
  IntegrationConnectionRow,
  IntegrationDemand,
  IntegrationRequestRow,
  IntegrationsState,
  Invoice,
  Lead,
  LeadDetail,
  LeadListItem,
  LeadQuery,
  LiveCall,
  Member,
  MyOperatorConnected,
  NoteLayer,
  NoteReading,
  ObjectionQuotes,
  Overview,
  PlatformHealth,
  PlatformInvoice,
  PlatformOrg,
  QaCall,
  QaCheck,
  QaLayer,
  QaMe,
  QaNext,
  QaRole,
  QaStage,
  QaSummary,
  RawCallRow,
  RecentEnquiries,
  Recording,
  RecordingCounts,
  ReviewAction,
  Role,
  Segment,
  Stage,
  Status,
  Task,
  TaskType,
  TaskWithLead,
  TeamHealth,
  Usage,
  WebhookFields,
  WebhookLeadRow,
  WebhookRetried,
  WebhookState,
} from "./types";

/** "solo": a team of one (D-169), Priya on her own, owner and telecaller. */
export type DemoRole = "owner" | "solo" | "manager" | "caller" | "platform";

const read = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string | null) => {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked: the demo still works for this page view.
  }
};

let ids = 0;
const newId = () => `10000000-0000-4000-8000-${String(++ids).padStart(12, "0")}`;

function e164(raw: string): string | null {
  const d = raw.replace(/[^\d+]/g, "");
  if (/^\d{10}$/.test(d) && /^[6-9]/.test(d)) return `+91${d}`;
  if (/^0\d{10}$/.test(d)) return `+91${d.slice(1)}`;
  if (/^91\d{10}$/.test(d)) return `+${d}`;
  if (/^\+[1-9]\d{7,14}$/.test(d)) return d;
  return null;
}

// ------------------------------------------------------------------ the CRM, as on the phone (D-163)

/** The demo company's stages: one for each older stage, in a real-estate company's words. */
const DEMO_STAGES: StageRef[] = [
  { id: "stage-new", name: "New", kind: "open", key: "new" },
  { id: "stage-contacted", name: "Contacted", kind: "open", key: "contacted" },
  { id: "stage-interested", name: "Interested", kind: "open", key: "interested" },
  { id: "stage-site_visit", name: "Visit", kind: "open", key: "site_visit" },
  { id: "stage-negotiating", name: "Negotiating", kind: "open", key: "negotiating" },
  { id: "stage-won", name: "Bought", kind: "won", key: "won" },
  { id: "stage-lost", name: "Lost", kind: "lost", key: "lost" },
  { id: "stage-not_a_lead", name: "Not a customer", kind: "lost", key: "not_a_lead" },
];

const stageOf = (stage: Stage): StageRef => DEMO_STAGES.find((s) => s.key === stage) ?? DEMO_STAGES[0];

function statusOf(stage: Stage): CustomerStatus {
  if (stage === "won" || stage === "lost" || stage === "not_a_lead") return stage;
  return "open";
}

/** The types booked beside the customer's one call follow-up (the database's tasks_one_open_per_lead). */
const BESIDE: string[] = ["visit", "meeting"];

/** The reason line when she gives none. */
const REASONS: Record<FollowUpKind, string> = {
  call: "Call back",
  visit: "Visit",
  meeting: "Meeting",
  send: "Send documents",
  collect: "Collect payment",
};

/** A follow-up as the demo keeps it: the task row and what the phone's CRM adds to it (D-163). */
type DemoTask = Task & {
  due_has_time?: boolean;
  place?: string | null;
  booked_by?: string | null;
  result?: VisitResult | null;
  /** Who closed it, for its history. */
  closed_by?: string | null;
};

interface DemoNumber {
  id: string;
  lead_id: string;
  e164: string;
  label: string | null;
  is_primary: boolean;
  is_whatsapp: boolean;
  contact_id: string | null;
  created_at: string;
}

/** What the database keeps worked out on a lead (refresh_lead_rollups), and its CRM state. */
interface CrmFacts {
  calls: number;
  connected: number;
  lastConnected: string | null;
  lastContact: string | null;
  open: DemoTask[];
  next: DemoTask | null;
  status: CustomerStatus;
  label: Label | null;
  numbers: DemoNumber[];
  assignedAt: string | null;
}

interface DemoContact {
  id: string;
  lead_id: string;
  name: string | null;
  role: string | null;
  is_primary: boolean;
  created_at: string;
}

const DEMO_TEMPLATES: MessageTemplate[] = [
  {
    id: "template-brochure",
    name: "Brochure",
    body: {
      en: "Hi {name}, here is the brochure for Green Meadows in Whitefield, as promised. – {caller}, Sharma Realty",
    },
    link: null,
    for_visits: false,
  },
  {
    id: "template-visit",
    name: "Visit confirmation",
    body: {
      en: "Hi {name}, your site visit is confirmed for {day} at {time}. Our office is next to the Green Meadows " +
        "gate; call me when you reach.\n{map}\n{handler} will meet you. – {caller}, Sharma Realty",
    },
    link: null,
    for_visits: true,
  },
  {
    id: "template-missed",
    name: "Sorry I missed your call",
    body: { en: "Hi {name}, sorry I missed your call. When is a good time to talk? – {caller}, Sharma Realty" },
    link: null,
    for_visits: false,
  },
];

const displayName = (l: Lead) => l.name?.trim() || l.phone_e164;

/** "3BHK Kharghar · ₹1.2 Cr": the first two of requirement, location and budget, as private.customer_context(). */
function contextOf(facts: Json): string | null {
  const f = (facts && typeof facts === "object" && !Array.isArray(facts) ? facts : {}) as Record<string, unknown>;
  const parts = ["requirement", "location", "budget"]
    .map((k) => f[k])
    .filter((v): v is string => typeof v === "string" && v.trim() !== "")
    .slice(0, 2)
    .map((v) => v.trim().slice(0, 40));
  return parts.length ? parts.join(" · ") : null;
}

const connectedCall = (c: Call) =>
  (c.call_status ??
    (c.duration_s > 0 && (c.direction === "incoming" || c.direction === "outgoing") ? "connected" : null)) ===
    "connected";

function addDay(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** percentile_cont(0.5), rounded to one decimal. */
function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const mid = (s.length - 1) / 2;
  const v = s[Math.floor(mid)] + (s[Math.ceil(mid)] - s[Math.floor(mid)]) * (mid - Math.floor(mid));
  return Math.round(v * 10) / 10;
}

/** Ascending, nulls last; dir -1 for descending (still nulls last). */
function nullsLast(a: number | null, b: number | null, dir: 1 | -1): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
  return (a - b) * dir;
}

const ms = (iso: string | null | undefined) => (iso ? Date.parse(iso) : null);

export class DemoApi implements Api {
  readonly demo = true;
  private db: DemoDb = buildDemo();
  private listeners = new Set<() => void>();
  /** Integrations (D-146): the company's requests, one of them made two days ago, and who opened what. */
  private integrationRequests: IntegrationRequestRow[] = [{
    key: "tradeindia",
    use_case: "leads_in",
    note: "Most of our enquiries come from TradeIndia.",
    requested_at: new Date(Date.now() - 2 * 86_400_000).toISOString(),
    requested_by: "Rahul Verma",
    notified_at: null,
    notes: [],
  }];
  private integrationOpens: { key: string; member_id: string; at: string }[] = [];
  private integrationConnections: IntegrationConnectionRow[] = [];
  private facebook: FacebookState | null = null;
  /** Each integration's activity, newest last (D-157). */
  private integrationLog: Record<string, IntegrationActivityItem[]> = {};
  /** What each paused integration was before (D-157). */
  private pausedFrom: Record<string, IntegrationConnectionRow["status"]> = {};
  /** The webhook integrations (D-158), JustDial first, with what arrived at their addresses. */
  private webhooks: Record<string, WebhookState> = {};
  /** IndiaMART's daily read (D-176): whether the demo company has kept a key, and what the last read found. */
  private indiamartPull: IndiaMartPull = { key: false };
  private webhookSeq = 0;
  /** The CRM as on the phone (D-163): what the demo's older tables do not hold. */
  private crm = this.seedCrm();
  /** The company's dispositions (D-188): none, so the standard choices, until an owner writes some. */
  private dispositionLists: Dispositions = { answered: [], unanswered: [] };
  /** The company's WhatsApp messages (D-187), in its order; a removed one is kept, inactive. */
  private templates: (MessageTemplate & { active: boolean })[] = DEMO_TEMPLATES.map((t) => ({
    ...structuredClone(t),
    active: true,
  }));

  private seedCrm() {
    const labels = new Map<string, { label: Label; at: string }>();
    const lostReasons = new Map<string, string | null>();
    for (const l of this.db.leads) {
      // Her own Hot and Warm on the customers she has spoken to, as she would have marked them after the call.
      const called = this.db.calls.some((c) => c.lead_id === l.id);
      if (called && (l.interest === "hot" || l.interest === "warm")) {
        labels.set(l.id, { label: l.interest, at: l.last_contact_at ?? l.created_at });
      }
      // The demo's lost customers booked a flat elsewhere.
      if (l.stage === "lost") lostReasons.set(l.id, "Bought elsewhere");
    }
    return {
      labels,
      lostReasons,
      /** When a customer was won or lost (else: their last call). */
      closedAt: new Map<string, string | null>(),
      /** When a customer was last given to someone. */
      assignedAt: new Map<string, string | null>(),
      /** Numbers and people added on the customer's page; the main ones come from the customer itself. */
      numbers: [] as DemoNumber[],
      contacts: [] as DemoContact[],
      /** History the demo's actions add (stages, labels, owners, notes, messages), oldest first. */
      events: [] as (TimelineEvent & { lead_id: string; seq: number })[],
      seq: 0,
    };
  }

  private get role(): DemoRole | null {
    return (read("callscribe.demo.role") as DemoRole | null) ?? null;
  }

  /** Demo sign-in: pick who to be. */
  signInAs(role: DemoRole): void {
    write("callscribe.demo.role", role);
    write("callscribe.demo.mfa", null);
    this.listeners.forEach((l) => l());
  }

  private me(): Member {
    const role = this.role;
    if (!role || role === "platform") {
      throw new ApiError("Please sign in.", "signed_out");
    }
    if (role === "solo") return { ...this.db.members[2], role: "owner" };
    return role === "owner" ? this.db.members[0] : role === "manager" ? this.db.members[1] : this.db.members[2];
  }

  private isManager() {
    return ["owner", "admin", "manager"].includes(this.me().role);
  }

  private requireAdmin() {
    if (!["owner", "admin"].includes(this.me().role)) {
      throw new ApiError("Only owners and admins can do that.", "42501");
    }
  }

  private requireManager() {
    if (!this.isManager()) {
      throw new ApiError(
        "Only owners, admins and managers can do that.",
        "42501",
      );
    }
  }

  private requirePlatform() {
    if (this.role !== "platform" || read("callscribe.demo.mfa") !== "done") {
      throw new ApiError("Platform functions need a second factor.", "42501");
    }
  }

  private visibleLead(id: string) {
    const lead = this.db.leads.find((l) => l.id === id);
    if (!lead) return null;
    if (this.isManager()) return lead;
    return this.callerSees(lead, this.me().id) ? lead : null;
  }

  /**
   * D-163, as private.can_see_lead(): a telecaller sees the customers she owns and those with an open follow-up of
   * hers (the demo shares none). Having called a customer, or a closed follow-up, no longer opens it.
   */
  private callerSees(lead: Lead, memberId: string) {
    return lead.owner_member_id === memberId ||
      this.db.tasks.some((t) => t.lead_id === lead.id && t.member_id === memberId && t.status === "open");
  }

  private visibleLeads() {
    return this.db.leads.filter((l) => this.visibleLead(l.id));
  }

  private mine<T extends { member_id: string | null }>(rows: T[]): T[] {
    if (this.isManager()) return rows;
    const me = this.me().id;
    return rows.filter((r) => r.member_id === me);
  }

  private categoryLabel(category: string): string {
    return this.db.taxonomies.find((t) => t.category === category)?.label ?? category;
  }

  private visibleTask(id: string): Task {
    const task = this.mine(this.db.tasks).find((t) => t.id === id);
    if (!task) throw new ApiError("No such task.", "P0002");
    if (task.status !== "open") {
      throw new ApiError("This task is already closed.", "22023");
    }
    return task;
  }

  // ---------------------------------------------------------------- session

  async hasSession() {
    return this.role !== null;
  }

  onSessionChange(listener: () => void) {
    this.listeners.add(listener);
    return () => void this.listeners.delete(listener);
  }

  async sendCode() {}

  async verifyCode() {
    this.signInAs("owner");
  }

  async signOut() {
    write("callscribe.demo.role", null);
    write("callscribe.demo.mfa", null);
    this.listeners.forEach((l) => l());
  }

  async status(): Promise<Status> {
    const role = this.role;
    if (!role) throw new ApiError("Please sign in.", "signed_out");
    if (role === "platform") {
      return {
        user_id: "demo-platform",
        platform_admin: true,
        platform_ready: read("callscribe.demo.mfa") === "done",
        member: null,
      };
    }
    const m = this.me();
    return {
      user_id: m.auth_user_id!,
      platform_admin: false,
      platform_ready: false,
      member: {
        member_id: m.id,
        display_name: m.display_name,
        role: m.role,
        org_id: this.db.org.id,
        org_name: this.db.org.name,
        org_status: "active",
        suspended_reason: null,
        timezone: this.db.org.timezone,
        under_review: false,
        member_limit: role === "solo" ? 1 : null,
        recordings: true,
        visits: this.visitPublic(),
        ...this.aiStatus(m.role),
      },
    };
  }

  /** The demo company's visit settings as my_status() gives them (D-186). */
  private visitPublic(): VisitPublic {
    const v = parseVisitSettings((this.db.org.settings as Record<string, Json> | null)?.visits);
    const one = v.handler === "one" ? this.activeMember(v.handler_member) : undefined;
    return {
      handler: v.handler,
      handler_member: one ? { id: one.id, name: one.display_name } : null,
      place: v.place.trim() || null,
      map_link: v.map_link.trim() || null,
      reminder: v.reminder,
      reminder_hours: v.reminder_hours,
    };
  }

  /** Who takes a visit booked for a time, as private.visit_handler(): whoever booked it, the one person, or in turn
   * whoever is free then and has fewest that day. */
  private visitHandler(booker: string, dueAt: string): string {
    const v = parseVisitSettings((this.db.org.settings as Record<string, Json> | null)?.visits);
    if (v.handler === "one") return this.activeMember(v.handler_member)?.id ?? booker;
    if (v.handler !== "in_turn") return booker;
    const due = Date.parse(dueAt);
    const day = localDate(new Date(due), this.db.org.timezone);
    const people = v.handler_members.map((id: string) => this.activeMember(id)).filter((m) => m !== undefined);
    const busy = (id: string) =>
      this.db.tasks.some((t) =>
        t.member_id === id && t.status === "open" && BESIDE.includes(t.type) &&
        Math.abs(Date.parse(t.due_at) - due) < 3_600_000
      );
    const thatDay = (id: string) =>
      this.db.tasks.filter((t) =>
        t.member_id === id && (t.type as string) === "visit" && t.status !== "cancelled" &&
        localDate(new Date(t.due_at), this.db.org.timezone) === day
      ).length;
    const sorted = [...people].sort((a, b) =>
      Number(busy(a!.id)) - Number(busy(b!.id)) || thatDay(a!.id) - thatDay(b!.id)
    );
    return sorted[0]?.id ?? booker;
  }

  // Mirrors my_status(): AI is on until the month's minutes are used up; owners and admins see the numbers.
  private aiStatus(role: Role) {
    const left = this.db.org.ai_minutes_included - this.db.usageByDay.reduce((a, b) => a + b.minutes, 0) +
      this.invoices.filter((i) => i.kind === "topup" && i.status === "paid" && i.paid_at! >= monthStart())
        .reduce((a, i) => a + ((i.lines as unknown as { minutes?: number }[])[0]?.minutes ?? 0), 0);
    return {
      ai_state: left <= 0 ? "minutes_used_up" as const : "on" as const,
      minutes: role === "owner" || role === "admin" ? { left, included: this.db.org.ai_minutes_included } : null,
      features: { ...this.addOns },
    };
  }

  /** The demo company's add-ons: both, unless set otherwise (D-181); kept with the demo, so a reload keeps them. */
  private get addOns(): { coach: boolean; autopilot: boolean } {
    const raw = read("callscribe.demo.addons");
    return raw === "crm" ? { coach: false, autopilot: false } : { coach: true, autopilot: raw !== "coach" };
  }

  setDemoAddOns(addOns: { coach: boolean; autopilot: boolean }) {
    write(
      "callscribe.demo.addons",
      addOns.autopilot ? null : addOns.coach ? "coach" : "crm",
    );
  }

  /** As the database: without Coach or Autopilot the AI's settings are locked (D-181). */
  private requireAi() {
    if (!this.addOns.coach && !this.addOns.autopilot) {
      throw new ApiError("AI is not enabled for your organization.", "42501");
    }
  }

  async refresh() {}

  async mfaState(): Promise<MfaState> {
    const done = read("callscribe.demo.mfa") === "done";
    const enrolled = read("callscribe.demo.mfa") !== null;
    return {
      current: done ? "aal2" : "aal1",
      next: "aal2",
      verifiedFactorId: enrolled ? "demo-factor" : null,
    };
  }

  async mfaEnroll() {
    write("callscribe.demo.mfa", "enrolled");
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" fill="#fff"/>` +
      `<text x="60" y="64" font-size="12" text-anchor="middle" font-family="sans-serif">Demo QR</text></svg>`;
    return {
      factorId: "demo-factor",
      qr: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
      secret: "DEMO SECRET",
    };
  }

  async mfaVerify(_factorId: string, code: string) {
    if (!/^\d{6}$/.test(code.trim())) {
      throw new ApiError("Enter the 6-digit code from your authenticator app.");
    }
    write("callscribe.demo.mfa", "done");
  }

  async createOrganization() {
    throw new ApiError("The demo already has an organization.");
  }

  // ------------------------------------------------------------- org data

  async org() {
    this.me();
    return { ...this.db.org };
  }

  async members() {
    this.me();
    return [...this.db.members].sort((a, b) => a.display_name.localeCompare(b.display_name));
  }

  async objectionQuotes(category: string): Promise<ObjectionQuotes> {
    const weekAgo = new Date(Date.now() - 7 * 86_400_000);
    const rows = this.mine(this.db.insights)
      .filter((i) => i.kind === "objection" && i.category === category && new Date(i.created_at) > weekAgo)
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    const leads = this.visibleLeads();
    return {
      category,
      label: this.categoryLabel(category),
      total: rows.length,
      rows: rows.slice(0, 100).map((i) => {
        const l = leads.find((x) => x.id === i.lead_id);
        return { ...i, lead: l ? { id: l.id, name: l.name, phone_e164: l.phone_e164 } : null };
      }),
    };
  }

  async overview(): Promise<Overview> {
    const now = new Date();
    const today = localDate(now, DEMO_TZ);
    const dayOf = (iso: string) => localDate(new Date(iso), DEMO_TZ);
    const calls = this.mine(this.db.calls);
    const recs = this.mine(this.db.recordings);
    const connected = (c: { duration_s: number; direction: string }) =>
      c.duration_s > 0 &&
      (c.direction === "incoming" || c.direction === "outgoing");
    const yesterday = new Date(now.getTime() - 86_400_000);
    const yDay = localDate(yesterday, DEMO_TZ);
    const weekAgo = new Date(now.getTime() - 7 * 86_400_000);
    const hotRecs = (from: Date, onlyDay?: string) =>
      recs.filter((r) =>
        this.db.analyses.some((a) => a.recording_id === r.id && a.interest === "hot") &&
        (onlyDay ? dayOf(r.recorded_at!) === onlyDay : new Date(r.recorded_at!) > from)
      ).length;
    const totals = (cs: typeof calls) => ({
      calls: cs.length,
      connected: cs.filter(connected).length,
      talk_minutes: Math.floor(cs.reduce((a, c) => a + c.duration_s, 0) / 60),
    });
    const tasks = this.mine(this.db.tasks);
    const weekDone = tasks.filter((t) =>
      t.status === "done" && t.completed_at &&
      new Date(t.completed_at) > weekAgo
    );
    const onTime = (t: Task) =>
      new Date(t.completed_at!).getTime() <=
        new Date(t.due_at).getTime() + 15 * 60_000;
    const objections = new Map<string, number>();
    for (const i of this.mine(this.db.insights)) {
      if (
        i.kind === "objection" && i.category && new Date(i.created_at) > weekAgo
      ) {
        objections.set(i.category, (objections.get(i.category) ?? 0) + 1);
      }
    }
    const visibleMembers = this.isManager() ? this.db.members : [this.me()];
    return {
      today,
      yesterday: {
        ...totals(calls.filter((c) => dayOf(c.started_at) === yDay)),
        hot_leads: hotRecs(yesterday, yDay),
      },
      week: {
        ...totals(calls.filter((c) => new Date(c.started_at) > weekAgo)),
        hot_leads: hotRecs(weekAgo),
        tasks_done: weekDone.length,
        tasks_done_on_time: weekDone.filter(onTime).length,
      },
      now: {
        overdue_tasks: tasks.filter((t) => t.status === "open" && new Date(t.due_at) < now)
          .length,
        due_today: tasks.filter((t) => t.status === "open" && dayOf(t.due_at) === today)
          .length,
        unreturned_missed_calls:
          tasks.filter((t) => t.status === "open" && t.source_rule?.startsWith("missed_call:")).length,
        hot_leads_open:
          this.visibleLeads().filter((l) => l.interest === "hot" && ACTIVE_STAGES.includes(l.stage)).length,
      },
      top_objections: [...objections.entries()].sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([category, count]) => ({
          category,
          label: this.categoryLabel(category),
          count,
        })),
      members: visibleMembers.filter((m) => m.status === "active").map((m) => {
        const mc = this.db.calls.filter((c) => c.member_id === m.id && new Date(c.started_at) > weekAgo);
        const done = weekDone.filter((t) => t.member_id === m.id);
        const device = this.db.devices.find((d) => d.member_id === m.id && !d.revoked_at);
        return {
          member_id: m.id,
          name: m.display_name,
          role: m.role,
          ...totals(mc),
          tasks_done: done.length,
          tasks_done_on_time: done.filter(onTime).length,
          device_last_seen: device?.last_seen_at ?? null,
        };
      }).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }

  async integrationsState(): Promise<IntegrationsState> {
    this.requireAdmin();
    return {
      country: "IN",
      requests: structuredClone(this.integrationRequests),
      connections: structuredClone(this.integrationConnections),
    };
  }

  async requestIntegration(key: string, useCase: UseCase | null, note: string | null): Promise<void> {
    this.requireAdmin();
    const me = this.me();
    const text = note?.trim() || null;
    if (!/^[a-z0-9][a-z0-9-]{1,39}$/.test(key)) throw new ApiError("Unknown integration.", "22023");
    if (text && text.length > 200) throw new ApiError("Keep the note to 200 characters.", "22023");
    const request = this.integrationRequests.find((r) => r.key === key);
    if (!request) {
      if (!useCase) throw new ApiError("Say what it should do.", "22023");
      this.integrationRequests.push({
        key,
        use_case: useCase,
        note: text,
        requested_at: new Date().toISOString(),
        requested_by: me.display_name,
        notified_at: null,
        notes: [],
      });
    } else if (text) {
      request.notes.push({ note: text, by: me.display_name, at: new Date().toISOString() });
    }
  }

  /** The demo's MyOperator: its users are the demo's telecallers, and a week's IVR calls are matched at once. */
  async connectMyOperator(token: string, ivrNumbers: string[]): Promise<MyOperatorConnected> {
    this.requireAdmin();
    if (!/^[A-Za-z0-9._-]{8,200}$/.test(token.trim())) {
      throw new ApiError("That does not look like a MyOperator API token.", "22023");
    }
    const numbers = ivrNumbers.map((n) => e164(n)).filter((n): n is string => n !== null);
    if (numbers.length !== ivrNumbers.filter((n) => n.trim()).length) {
      throw new ApiError("Give each IVR number as the phone shows it, like 011 4000 4999.", "22023");
    }
    const callers = this.db.members.filter((m) => m.role === "caller");
    const users = [
      ...callers.map((m, i) => ({
        id: `mo-${i + 1}`,
        alt_id: null,
        name: m.display_name,
        number: m.phone_e164,
        member_id: m.id,
      })),
      { id: "mo-desk", alt_id: null, name: "Front desk", number: "+919810000001", member_id: null },
    ];
    const ivr = numbers.length ? numbers : ["+911140004999"];
    this.integrationConnections = [...this.integrationConnections.filter((c) => c.key !== "myoperator"), {
      key: "myoperator",
      status: "connected",
      connected_at: new Date().toISOString(),
      last_sync_at: new Date().toISOString(),
      last_error: null,
      settings: {
        users,
        ivr_numbers: ivr,
        ivr_removed: [],
        moved: 12,
        last_result: { moved: 12, ambiguous: 1, skipped: 0, new_ivr_numbers: numbers.length ? [] : ivr },
      },
    }];
    return {
      users: users.length,
      matched: callers.length,
      ivr_numbers: ivr,
      sync: { logs: 140, moved: 12 },
    };
  }

  async syncMyOperator() {
    this.requireAdmin();
    const c = this.integrationConnections.find((x) => x.key === "myoperator" && x.status !== "disconnected");
    if (!c) throw new ApiError("Connect it first.", "P0002");
    c.last_sync_at = new Date().toISOString();
    c.settings = { ...c.settings, moved: (c.settings?.moved ?? 0) + 1 };
    return { logs: 6, moved: 1 };
  }

  async updateMyOperatorSettings(ivrNumbers: string[], userMembers: Record<string, string | null>) {
    this.requireAdmin();
    const c = this.integrationConnections.find((x) => x.key === "myoperator");
    if (!c) throw new ApiError("Connect MyOperator first.", "P0002");
    const numbers = ivrNumbers.map((n) => e164(n));
    if (numbers.some((n) => n === null)) {
      throw new ApiError("Give each IVR number as the phone shows it, like 011 4000 4999.", "22023");
    }
    const kept = [...new Set(numbers as string[])].sort();
    const removed = [...new Set([...(c.settings?.ivr_removed ?? []), ...(c.settings?.ivr_numbers ?? [])])]
      .filter((n) => !kept.includes(n));
    c.settings = {
      ...c.settings,
      ivr_numbers: kept,
      ivr_removed: removed,
      users: (c.settings?.users ?? []).map((u) => (u.id in userMembers ? { ...u, member_id: userMembers[u.id] } : u)),
    };
  }

  async disconnectIntegration(key: string) {
    this.requireAdmin();
    const c = this.integrationConnections.find((x) => x.key === key);
    if (!c) throw new ApiError("It is not connected.", "P0002");
    c.status = "disconnected";
    const w = this.webhooks[key];
    if (w) {
      // Its address is refused at once, and what waited will not come in.
      w.status = "disconnected";
      w.address = null;
      for (const row of w.leads.filter((l) => l.status === "pending")) {
        row.status = "skipped";
        row.reason = "Turned off before it came in.";
      }
    }
    if (key === "indiamart") this.indiamartPull = { key: false };
    this.note(key, "integration.disconnected");
  }

  /** The demo's new leads (D-149): its enquiries with their time, or else its newest customers as if they had just
   * filled in a Facebook form, so the card has something to show. */
  async recentEnquiries(hours = 24): Promise<RecentEnquiries> {
    const me = this.me();
    const since = Date.now() - Math.min(Math.max(hours, 1), 24 * 7) * 3_600_000;
    const facebook = this.db.sources.find((x) => /facebook/i.test(x.name))?.name ?? "Facebook ad";
    const minutesAgo = [3, 47, 190, 610, 1500];
    const rows = this.db.enquiries.length
      ? this.db.enquiries.filter((e) => e.time_from_file).map((e) => ({
        id: e.id,
        lead_id: e.lead_id,
        at: e.enquired_at,
        source: this.db.sources.find((x) => x.id === e.source_id)?.name ?? null,
        platform: e.platform,
        campaign: e.campaign_name,
        form: e.form_name,
      }))
      : this.db.leads.slice(0, minutesAgo.length).map((l, i) => ({
        id: `demo-enquiry-${i}`,
        lead_id: l.id,
        at: new Date(Date.now() - minutesAgo[i] * 60_000).toISOString(),
        source: facebook,
        platform: i === 1 ? "ig" : "fb",
        campaign: "October launch",
        form: i % 2 ? "Brochure request" : "Site visit enquiry",
      }));
    const items = rows
      .filter((r) => Date.parse(r.at) > since)
      .map((r) => {
        const lead = this.db.leads.find((l) => l.id === r.lead_id)!;
        const called = this.db.calls
          .filter((c) => c.lead_id === lead.id && Date.parse(c.started_at) >= Date.parse(r.at))
          .map((c) => c.started_at)
          .sort()[0] ?? null;
        return {
          enquiry_id: r.id,
          lead_id: lead.id,
          name: lead.name ?? lead.phone_e164,
          phone: lead.phone_e164,
          enquired_at: r.at,
          source: r.source,
          platform: r.platform,
          campaign: r.campaign,
          form: r.form,
          owner: this.db.members.find((m) => m.id === lead.owner_member_id)?.display_name ?? null,
          owner_id: lead.owner_member_id,
          new_customer: true,
          called_at: called,
        };
      })
      .filter((e) => this.isManager() || e.owner_id === me.id)
      .sort((a, b) => b.enquired_at.localeCompare(a.enquired_at));
    return {
      since: new Date(since).toISOString(),
      total: items.length,
      not_called: items.filter((e) => !e.called_at).length,
      items: items.slice(0, 50),
    };
  }

  /** The demo's Facebook (D-148): the login comes straight back, with three Pages to choose from. */
  async facebookState(): Promise<FacebookState> {
    this.requireAdmin();
    const fb = structuredClone(this.facebook ?? emptyFacebook());
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const week = Date.now() - 7 * 86_400_000;
    const at = (l: FacebookLeadRow) => new Date(l.created_time ?? l.received_at).getTime();
    fb.counts = {
      waiting: fb.leads.filter((l) => l.status === "pending").length,
      today: fb.leads.filter((l) => l.status === "done" && at(l) >= today.getTime()).length,
      week: fb.leads.filter((l) => l.status === "done" && at(l) > week).length,
      skipped_week: fb.leads.filter((l) => l.status === "skipped" && at(l) > week).length,
      failed_week: fb.leads.filter((l) => l.status === "failed" && at(l) > week).length,
    };
    const firstCall = Math.min(...this.db.calls.map((c) => new Date(c.started_at).getTime()));
    fb.calls_since = Number.isFinite(firstCall) ? new Date(firstCall).toISOString() : null;
    for (const p of fb.pages) {
      const latest = fb.leads.filter((l) => l.page_id === p.id && l.status === "done").map(at);
      p.last_lead_at = latest.length ? new Date(Math.max(...latest)).toISOString() : null;
    }
    return fb;
  }

  async facebookStart(redirectUri: string): Promise<string> {
    this.requireAdmin();
    if (!/\/integrations$/.test(redirectUri)) throw new ApiError("Facebook cannot come back to that address.", "22023");
    return "#/integrations?code=demo-code&state=demo-state";
  }

  async facebookFinish(code: string, state: string) {
    this.requireAdmin();
    if (code !== "demo-code" || state !== "demo-state") {
      throw new ApiError("That Facebook login has expired. Start again.", "22023");
    }
    const callers = this.db.members.filter((m) => m.role === "caller" && m.status === "active").map((m) => m.id);
    this.facebook = {
      ...(this.facebook ?? emptyFacebook()),
      status: this.facebook?.status === "error" ? "error" : "connected",
      connected_at: this.facebook?.connected_at ?? new Date().toISOString(),
      facebook_user: this.me().display_name,
      login_expires_at: Math.floor(Date.now() / 1000) + 60 * 86_400,
    };
    if (this.facebook.assign.members.length === 0 && this.facebook.assign.mode === "none" && callers.length) {
      this.facebook.assign = { mode: "split", members: callers };
    }
    this.setFacebookConnection();
    this.note("facebook-lead-ads", "integration.connected", { facebook_user: this.facebook.facebook_user });
    return { facebook_user: this.facebook.facebook_user, pages: await this.facebookPages() };
  }

  async facebookPages(): Promise<FacebookPageChoice[]> {
    this.requireAdmin();
    if (!this.facebook || this.facebook.status === "disconnected") throw new ApiError("Connect it first.", "P0002");
    return DEMO_FACEBOOK_PAGES.map((p) => ({ ...p, connected: this.facebook!.pages.some((x) => x.id === p.id) }));
  }

  async facebookChoose(pageIds: string[]): Promise<FacebookChosen[]> {
    this.requireAdmin();
    const fb = this.facebook;
    if (!fb || fb.status === "disconnected") throw new ApiError("Connect it first.", "P0002");
    const results: FacebookChosen[] = [];
    for (const id of pageIds) {
      const page = DEMO_FACEBOOK_PAGES.find((p) => p.id === id);
      if (!page) {
        results.push({ id, error: "This Facebook login does not manage that Page." });
      } else if (!page.may_read_leads) {
        results.push({ id, name: page.name, error: "Facebook did not allow it: you cannot advertise for this Page." });
      } else {
        const now = new Date().toISOString();
        fb.pages = [...fb.pages.filter((p) => p.id !== id), {
          id,
          name: page.name,
          status: "active",
          last_error: null,
          form_ids: fb.pages.find((p) => p.id === id)?.form_ids ?? null,
          connected_at: now,
          checked_at: now,
          last_lead_at: fb.leads.length ? fb.leads[0].created_time : null,
          forms: fb.pages.find((p) => p.id === id)?.forms ?? DEMO_FACEBOOK_FORMS.map((f) => ({ ...f })),
        }];
        results.push({ id, name: page.name, forms: DEMO_FACEBOOK_FORMS.length });
        this.note("facebook-lead-ads", "integration.changed", { page_added: page.name });
      }
    }
    if (fb.leads.length === 0 && fb.pages.length) fb.leads = this.demoFacebookLeads(fb.pages[0].id);
    if (fb.status !== "paused") fb.status = fb.pages.some((p) => p.status === "error") ? "error" : "connected";
    fb.last_sync_at = new Date().toISOString();
    this.setFacebookConnection();
    return results;
  }

  async facebookRemovePage(pageId: string) {
    this.requireAdmin();
    if (!this.facebook) throw new ApiError("Connect it first.", "P0002");
    const page = this.facebook.pages.find((p) => p.id === pageId);
    this.facebook.pages = this.facebook.pages.filter((p) => p.id !== pageId);
    this.setFacebookConnection();
    if (page) this.note("facebook-lead-ads", "integration.changed", { page_removed: page.name ?? pageId });
  }

  async facebookSetForms(pageId: string, formIds: string[] | null) {
    this.requireAdmin();
    const page = this.facebook?.pages.find((p) => p.id === pageId);
    if (!page) throw new ApiError("That Page is not connected.", "P0002");
    if (formIds !== null && formIds.length === 0) {
      throw new ApiError("Choose at least one form, or every form.", "22023");
    }
    page.form_ids = formIds === null ? null : [...new Set(formIds)].sort();
    this.note("facebook-lead-ads", "integration.changed", { page: pageId, forms: page.form_ids });
  }

  async facebookSetAssignment(mode: FacebookAssign["mode"], members: string[]) {
    this.requireAdmin();
    if (!this.facebook || this.facebook.status === "disconnected") {
      throw new ApiError("Connect Facebook first.", "P0002");
    }
    const active = new Set(this.db.members.filter((m) => m.status === "active").map((m) => m.id));
    if (
      mode !== "none" &&
      (members.length === 0 || members.some((m) => !active.has(m)) || (mode === "one" && members.length !== 1))
    ) {
      throw new ApiError("Leads can go only to active members of your team.", "22023");
    }
    this.facebook.assign = { mode, members: mode === "none" ? [] : members };
    this.note("facebook-lead-ads", "integration.changed", { assign: mode, ...this.people(members) });
  }

  async facebookSetFormAssignment(formId: string, mode: FacebookAssign["mode"] | "default", members: string[]) {
    this.requireAdmin();
    const form = this.facebook?.pages.flatMap((p) => p.forms).find((f) => f.id === formId);
    if (!form) throw new ApiError("That form is not on a Page your company connected.", "P0002");
    const active = new Set(this.db.members.filter((m) => m.status === "active").map((m) => m.id));
    if (
      (mode === "one" || mode === "split") &&
      (members.length === 0 || members.some((m) => !active.has(m)) || (mode === "one" && members.length !== 1))
    ) {
      throw new ApiError("Leads can go only to active members of your team.", "22023");
    }
    form.assign = mode === "default" ? null : { mode, members: mode === "none" ? [] : members };
    this.note("facebook-lead-ads", "integration.changed", {
      form: formId,
      form_name: form.name,
      assign: mode,
      ...this.people(mode === "one" || mode === "split" ? members : []),
    });
  }

  async pauseIntegration(key: string) {
    this.requireAdmin();
    const c = this.integrationConnections.find((x) =>
      x.key === key && ["connected", "error", "waiting"].includes(x.status)
    );
    if (!c) throw new ApiError("Only a connected integration can be paused.", "P0002");
    this.pausedFrom[key] = c.status;
    c.status = "paused";
    if (key === "facebook-lead-ads" && this.facebook) this.facebook.status = "paused";
    if (this.webhooks[key]) this.webhooks[key].status = "paused";
    this.note(key, "integration.paused");
  }

  async resumeIntegration(key: string) {
    this.requireAdmin();
    const c = this.integrationConnections.find((x) => x.key === key && x.status === "paused");
    if (!c) throw new ApiError("It is not paused.", "P0002");
    c.status = this.pausedFrom[key] === "waiting" ? "waiting" : "connected";
    if (key === "facebook-lead-ads" && this.facebook) this.facebook.status = "connected";
    this.note(key, "integration.resumed");
    const w = this.webhooks[key];
    if (w) {
      // What arrived while paused comes in now.
      w.status = c.status;
      this.webhookWaiting(key);
    }
  }

  async integrationActivity(key: string): Promise<IntegrationActivityItem[]> {
    this.requireAdmin();
    return structuredClone(this.integrationLog[key] ?? []).reverse();
  }

  /** A change to an integration, in its activity (D-157): a person's, or what Voxowa itself saw. */
  private note(
    key: string,
    action: string,
    detail: Record<string, unknown> = {},
    kind: "member" | "system" = "member",
  ) {
    (this.integrationLog[key] ??= []).push({
      at: new Date().toISOString(),
      action,
      kind,
      who: kind === "system" ? "Voxowa" : this.me().display_name,
      detail,
    });
  }

  /** The people a choice names, with their names as the activity shows them. */
  private people(ids: string[]) {
    return {
      members: ids,
      member_names: ids.map((id) => this.db.members.find((m) => m.id === id)?.display_name ?? "–"),
    };
  }

  async facebookImport(days: 7 | 30 | 90, assign: boolean) {
    this.requireAdmin();
    if (!this.facebook?.pages.length) throw new ApiError("Connect it first.", "P0002");
    const now = new Date().toISOString();
    this.facebook.import = {
      status: "done",
      days,
      assign,
      started_at: now,
      finished_at: now,
      leads: days * 2,
      added: Math.round(days * 1.6),
      skipped: Math.round(days / 30),
    };
    this.note("facebook-lead-ads", "integration.changed", { import_days: days, assign });
  }

  async facebookSync() {
    this.requireAdmin();
    if (!this.facebook?.pages.length) throw new ApiError("Connect it first.", "P0002");
    const now = new Date().toISOString();
    this.facebook.last_sync_at = now;
    for (const p of this.facebook.pages) p.checked_at = now;
  }

  async facebookDisconnect() {
    this.requireAdmin();
    if (!this.facebook) throw new ApiError("It is not connected.", "P0002");
    this.facebook = { ...emptyFacebook(), assign: this.facebook.assign };
    const c = this.integrationConnections.find((x) => x.key === "facebook-lead-ads");
    if (c) c.status = "disconnected";
    this.note("facebook-lead-ads", "integration.disconnected");
  }

  private setFacebookConnection() {
    const fb = this.facebook!;
    this.integrationConnections = [...this.integrationConnections.filter((c) => c.key !== "facebook-lead-ads"), {
      key: "facebook-lead-ads",
      status: fb.status,
      connected_at: fb.connected_at,
      last_sync_at: fb.last_sync_at,
      last_error: fb.last_error,
    }];
  }

  /** The demo's webhook integrations (D-158), JustDial first: the address comes at once, and a JustDial listing's
   * first three leads arrive 15 seconds later (two come in; one names its number in a way Voxowa does not know). */
  async webhookState(key: string): Promise<WebhookState> {
    this.requireAdmin();
    webhookKey(key);
    const w = this.webhooks[key];
    if (w?.status === "waiting" && !w.leads.length && Date.now() - Date.parse(w.connected_at ?? "") > 15_000) {
      this.webhookReceive(key, key === "indiamart" ? demoIndiaMartLeads().slice(0, 2) : demoWebhookLeads());
    }
    return this.webhookView(key);
  }

  async webhookConnect(key: string): Promise<WebhookState> {
    this.requireAdmin();
    webhookKey(key);
    const was = this.webhooks[key];
    if (was && was.status !== "disconnected") return this.webhookView(key);
    const callers = this.db.members.filter((m) => m.role === "caller" && m.status === "active").map((m) => m.id);
    this.webhooks[key] = {
      ...emptyWebhook(),
      status: "waiting",
      connected_at: new Date().toISOString(),
      address: demoWebhookAddress(),
      assign: was?.assign ?? { mode: callers.length ? "split" : "none", members: callers },
      fields: was?.fields ?? {},
      leads: was?.leads ?? [],
    };
    this.setWebhookConnection(key);
    this.note(key, "integration.connected", { address: "new" });
    return this.webhookView(key);
  }

  async webhookNewAddress(key: string): Promise<WebhookState> {
    this.requireAdmin();
    const w = this.liveWebhook(key);
    w.address = demoWebhookAddress();
    if (w.status === "paused") this.pausedFrom[key] = "waiting";
    else w.status = "waiting";
    this.setWebhookConnection(key);
    this.note(key, "integration.changed", { address: "renewed" });
    return this.webhookView(key);
  }

  async webhookSetAssignment(key: string, mode: FacebookAssign["mode"], members: string[]) {
    this.requireAdmin();
    const w = this.liveWebhook(key);
    const active = new Set(this.db.members.filter((m) => m.status === "active").map((m) => m.id));
    if (
      mode !== "none" &&
      (members.length === 0 || members.some((m) => !active.has(m)) || (mode === "one" && members.length !== 1))
    ) {
      throw new ApiError("Leads can go only to active members of your team.", "22023");
    }
    w.assign = { mode, members: mode === "none" ? [] : members };
    this.note(key, "integration.changed", { assign: mode, ...this.people(w.assign.members) });
  }

  async webhookSetFields(key: string, fields: WebhookFields): Promise<WebhookRetried> {
    this.requireAdmin();
    const w = this.liveWebhook(key);
    w.fields = Object.fromEntries(
      Object.entries(fields).filter(([, v]) => v?.trim()).map(([k, v]) => [k, v!.trim()]),
    ) as WebhookFields;
    this.note(key, "integration.changed", { fields: w.fields });
    return this.webhookWaiting(key);
  }

  async webhookRetry(key: string): Promise<WebhookRetried> {
    this.requireAdmin();
    this.liveWebhook(key);
    const tried = this.webhookWaiting(key);
    this.note(key, "integration.changed", { tried: tried.tried, brought_in: tried.brought_in });
    return tried;
  }

  /** IndiaMART's Pull API key (D-176): the demo takes any key of the right form, except one starting "wrong", which
   * IndiaMART refuses; its first read brings in one lead the push missed. */
  async indiamartSaveKey(key: string): Promise<IndiaMartRead> {
    this.requireAdmin();
    this.liveWebhook("indiamart");
    const k = key.trim();
    if (!/^[A-Za-z0-9+/=_-]{10,200}$/.test(k)) throw new ApiError("That does not look like an IndiaMART key.", "22023");
    if (/^wrong/i.test(k)) {
      throw new ApiError(
        "IndiaMART did not accept the key. It may be mistyped, unused for 7 days, or replaced by a newer one: make a " +
          "new key in IndiaMART and add it here.",
      );
    }
    // The read finds the pushed leads and one the push missed; only that one comes in.
    const now = new Date().toISOString();
    const id = (fields: [string, string][]) => fields.find(([k]) => k.endsWith("UNIQUE_QUERY_ID"))?.[1];
    const have = new Set(this.webhooks.indiamart.leads.map((l) => id(l.fields)));
    const read = demoIndiaMartLeads();
    const fresh = read.filter((l) => !have.has(id(l)));
    if (fresh.length) this.webhookReceive("indiamart", fresh);
    const found = fresh.length;
    const known = read.length - found;
    this.indiamartPull = { key: true, key_set_at: now, checked_at: now, outcome: "leads", found, known };
    this.note("indiamart", "integration.changed", { pull_key: "set" });
    return { outcome: "leads", new: found, known };
  }

  async indiamartCheckNow(): Promise<IndiaMartRead> {
    this.requireAdmin();
    this.liveWebhook("indiamart");
    const p = this.indiamartPull;
    if (!p.key) throw new ApiError("Add the Pull API key first.", "P0002");
    const last = Date.parse(p.checked_at ?? "");
    if (Date.now() - last < 5 * 60_000) {
      const minutes = Math.max(1, Math.ceil((last + 5 * 60_000 - Date.now()) / 60_000));
      throw new ApiError(`IndiaMART allows one check every 5 minutes. Try again in ${minutes} minutes.`, "55000");
    }
    this.indiamartPull = { ...p, checked_at: new Date().toISOString(), outcome: "none", found: 0, known: 0 };
    return { outcome: "none", new: 0, known: 0 };
  }

  async indiamartRemoveKey() {
    this.requireAdmin();
    this.indiamartPull = { key: false };
    this.note("indiamart", "integration.changed", { pull_key: "removed" });
  }

  /** Leads arriving at the demo's address, each as its fields: kept, then brought in unless it is paused. */
  webhookReceive(key: string, leads: [string, string][][]): "ok" | "paused" | "unknown" {
    const w = this.webhooks[key];
    if (!w || w.status === "disconnected") return "unknown";
    const now = new Date().toISOString();
    for (const fields of leads) {
      w.leads.unshift({
        id: ++this.webhookSeq,
        received_at: now,
        status: "pending",
        reason: null,
        lead_id: null,
        enquiry: null,
        name: null,
        phone: null,
        owner: null,
        fields,
      });
    }
    w.last_lead_at = now;
    // The first lead shows the address works (D-157); paused meanwhile, it is connected on resume.
    const first = w.status === "waiting" || (w.status === "paused" && this.pausedFrom[key] === "waiting");
    if (w.status === "waiting") w.status = "connected";
    else if (first) this.pausedFrom[key] = "connected";
    if (first) this.note(key, "integration.first_event", { from: "waiting", to: "connected" }, "system");
    this.setWebhookConnection(key);
    if (w.status === "paused") return "paused";
    this.webhookWaiting(key, false);
    return "ok";
  }

  private liveWebhook(key: string): WebhookState {
    const w = this.webhooks[key];
    if (!w || w.status === "disconnected") throw new ApiError("Turn it on first.", "P0002");
    return w;
  }

  private setWebhookConnection(key: string) {
    const w = this.webhooks[key];
    this.integrationConnections = [...this.integrationConnections.filter((c) => c.key !== key), {
      key,
      status: w.status,
      connected_at: w.connected_at,
      last_sync_at: w.last_lead_at,
      last_error: null,
    }];
  }

  /** The leads waiting to come in (and those that could not be read), oldest first. */
  private webhookWaiting(key: string, unreadable = true): WebhookRetried {
    const w = this.webhooks[key];
    let tried = 0;
    let broughtIn = 0;
    if (w.status !== "paused") {
      for (const row of [...w.leads].reverse()) {
        if (row.status !== "pending" && !(unreadable && row.status === "unreadable")) continue;
        tried++;
        if (this.webhookIntake(w, row)) broughtIn++;
      }
    }
    return { tried, brought_in: broughtIn, unreadable: w.leads.filter((l) => l.status === "unreadable").length };
  }

  /** One lead in, as the server reads it: its number, name and city by their usual names or the fields chosen. */
  private webhookIntake(w: WebhookState, row: WebhookLeadRow): boolean {
    // A field inside another ("RESPONSE.SENDER_MOBILE") is read by its own name, as the server does.
    const key = (name: string) => name.split(".").pop()!.toLowerCase().replace(/[^a-z0-9]/g, "");
    const pick = (names: string[], chosen?: string) =>
      (chosen ? row.fields.find(([k, v]) => k === chosen && v.trim())?.[1] : undefined) ??
        names.map((n) => row.fields.find(([k, v]) => key(k) === n && v.trim())?.[1]).find(Boolean) ?? null;
    const raw = pick(
      ["mobile", "mobileno", "mobilenumber", "phone", "phonenumber", "contactnumber", "sendermobile"],
      w.fields.phone,
    );
    const phone = raw ? e164(raw.split(/[,;/|]/)[0]) : null;
    if (!phone) {
      row.status = "unreadable";
      row.reason = raw ? "not a valid phone number" : "no phone number";
      return false;
    }
    let lead = this.db.leads.find((l) => l.phone_e164 === phone);
    if (!lead) {
      const now = new Date().toISOString();
      const people = w.assign.mode === "none" ? [] : w.assign.members;
      const owner = people.length ? people[(this.webhookSeq + this.db.leads.length) % people.length] : null;
      lead = {
        id: newId(),
        org_id: this.db.org.id,
        phone_e164: phone,
        name: pick(["name", "fullname", "customername", "sendername"], w.fields.name),
        owner_member_id: w.assign.mode === "one" ? people[0] : owner,
        stage: "new",
        interest: null,
        summary: null,
        facts: {},
        source: "integration",
        first_contact_at: null,
        last_contact_at: null,
        last_enquiry_at: now,
        source_id: null,
        campaign: null,
        created_at: now,
        updated_at: now,
      };
      this.db.leads.push(lead);
    }
    row.status = "done";
    row.reason = null;
    row.lead_id = lead.id;
    row.enquiry = true;
    return true;
  }

  /** What the page shows: the latest 20 leads with their customers, the counts and the fields seen lately. */
  private webhookView(key: string): WebhookState {
    const w = structuredClone(this.webhooks[key] ?? emptyWebhook());
    const all = w.leads;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const week = Date.now() - 7 * 86_400_000;
    const at = (row: WebhookLeadRow) => Date.parse(row.received_at);
    w.counts = {
      today: all.filter((l) => l.status === "done" && l.enquiry && at(l) >= today.getTime()).length,
      week: all.filter((l) => l.status === "done" && l.enquiry && at(l) > week).length,
      unreadable: all.filter((l) => l.status === "unreadable").length,
      waiting: all.filter((l) => l.status === "pending").length,
      skipped_week: all.filter((l) => l.status === "skipped" && at(l) > week).length,
    };
    const seen = new Map<string, number>();
    for (const [name] of all.slice(0, 50).flatMap((l) => l.fields)) seen.set(name, (seen.get(name) ?? 0) + 1);
    w.seen_fields = [...seen].sort((a, b) => b[1] - a[1]).map(([name]) => name);
    w.pull = key === "indiamart" ? { ...this.indiamartPull } : null;
    w.leads = all.slice(0, 20).map((row) => {
      const lead = row.lead_id ? this.db.leads.find((l) => l.id === row.lead_id) : undefined;
      return {
        ...row,
        name: lead?.name ?? null,
        phone: lead?.phone_e164 ?? null,
        owner: this.db.members.find((m) => m.id === lead?.owner_member_id)?.display_name ?? null,
      };
    });
    return w;
  }

  /** A few leads as the demo's first Page brought them: three in, one without a number. */
  private demoFacebookLeads(pageId: string): FacebookLeadRow[] {
    const leads = this.db.leads.slice(0, 3);
    const at = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString();
    const rows: FacebookLeadRow[] = leads.map((l, i) => ({
      id: String(900000000000001 + i),
      page_id: pageId,
      form: DEMO_FACEBOOK_FORMS[i % 2].name,
      via: "webhook",
      received_at: at(12 + i * 95),
      created_time: at(12 + i * 95),
      status: "done",
      reason: null,
      attempts: 1,
      lead_id: l.id,
      name: l.name,
      phone: l.phone_e164,
      owner: this.db.members.find((m) => m.id === l.owner_member_id)?.display_name ?? null,
    }));
    rows.splice(1, 0, {
      id: "900000000000009",
      page_id: pageId,
      form: DEMO_FACEBOOK_FORMS[1].name,
      via: "webhook",
      received_at: at(60),
      created_time: at(60),
      status: "skipped",
      reason: "no phone number",
      attempts: 1,
      lead_id: null,
      name: null,
      phone: null,
      owner: null,
    });
    return rows;
  }

  async openIntegration(key: string): Promise<void> {
    this.requireAdmin();
    this.integrationOpens.push({ key, member_id: this.me().id, at: new Date().toISOString() });
  }

  async dashboardRange(from: string, to: string): Promise<DashboardRange> {
    const me = this.me();
    if (to < from) throw new ApiError("Choose a first day on or before the last day.");
    const people = (this.isManager() ? this.db.members : [me]).filter((m) => m.status === "active");
    const ids = new Set(people.map((m) => m.id));
    const inRange = (iso: string) => {
      const d = localDate(new Date(iso), DEMO_TZ);
      return d >= from && d <= to;
    };
    const calls = this.mine(this.db.calls).filter((c) => ids.has(c.member_id) && inRange(c.started_at));
    const status = (c: (typeof calls)[number]) => c.call_status ?? "connected";
    const done = this.mine(this.db.tasks).filter((t) =>
      t.status === "done" && t.completed_at && t.member_id && ids.has(t.member_id) && inRange(t.completed_at)
    );
    const members = people.map((m) => {
      const cs = calls.filter((c) => c.member_id === m.id);
      const ds = done.filter((t) => t.member_id === m.id);
      const connected = cs.filter((c) => status(c) === "connected");
      const ends = cs.map((c) => new Date(c.started_at).getTime() + c.duration_s * 1000);
      return {
        member_id: m.id,
        name: m.display_name,
        role: m.role,
        dialed: cs.filter((c) => c.direction === "outgoing").length,
        dialed_connected: cs.filter((c) => c.direction === "outgoing" && status(c) === "connected").length,
        not_answered: cs.filter((c) => c.direction === "outgoing" && status(c) === "not_answered").length,
        received: cs.filter((c) => c.direction !== "outgoing" && status(c) === "connected").length,
        missed: cs.filter((c) => status(c) === "missed" || status(c) === "rejected").length,
        talk_seconds: connected.reduce((a, c) => a + c.duration_s, 0),
        customers_reached: new Set(connected.map((c) => c.lead_id).filter(Boolean)).size,
        interested: cs.filter((c) => c.outcome === "interested").length,
        call_back: cs.filter((c) => c.outcome === "call_back").length,
        visits_booked: cs.filter((c) => c.outcome === "visit_booked").length,
        follow_ups_done: ds.length,
        follow_ups_on_time: ds.filter((t) =>
          new Date(t.completed_at!).getTime() <= new Date(t.due_at).getTime() + 15 * 60_000
        ).length,
        first_call_at: cs.length ? cs.map((c) => c.started_at).sort()[0] : null,
        last_call_end_at: ends.length ? new Date(Math.max(...ends)).toISOString() : null,
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
    const bucket = (key: (c: (typeof calls)[number]) => string) => {
      const map = new Map<string, { calls: number; connected: number; talk_seconds: number }>();
      for (const c of calls) {
        const k = `${c.member_id}|${key(c)}`;
        const b = map.get(k) ?? { calls: 0, connected: 0, talk_seconds: 0 };
        b.calls++;
        if (status(c) === "connected") {
          b.connected++;
          b.talk_seconds += c.duration_s;
        }
        map.set(k, b);
      }
      return [...map.entries()].map(([k, b]) => ({ id: k.split("|")[0], key: k.split("|")[1], ...b }));
    };
    // By half hour (D-145): "10:30" is the second half of 10 am.
    const hours = bucket((c) => {
      const t = wallParts(new Date(c.started_at), DEMO_TZ);
      return `${t.h}:${t.min < 30 ? 0 : 30}`;
    }).map(({ id, key, ...b }) => ({
      member_id: id,
      hour: Number(key.split(":")[0]),
      minute: Number(key.split(":")[1]),
      ...b,
    }));
    const days = from === to ? [] : bucket((c) => localDate(new Date(c.started_at), DEMO_TZ))
      .map(({ id, key, ...b }) => ({ member_id: id, day: key, ...b }));
    return { from, to, timezone: DEMO_TZ, members, hours, days };
  }

  async leads(
    query: LeadQuery,
  ): Promise<{ rows: LeadListItem[]; total: number }> {
    const q = (query.search ?? "").trim().toLowerCase();
    const digits = q.replace(/\D/g, "");
    const rows = this.visibleLeads()
      .filter((l) =>
        !q || (l.name ?? "").toLowerCase().includes(q) ||
        (digits.length >= 4 && l.phone_e164.includes(digits))
      )
      .filter((l) => !query.stage || l.stage === query.stage)
      .filter((l) => !query.interest || l.interest === query.interest)
      .filter((l) =>
        !query.owner ||
        (query.owner === "none" ? !l.owner_member_id : l.owner_member_id === query.owner)
      )
      .filter((l) => !query.called || (query.called === "no") === !l.last_contact_at)
      .sort((a, b) =>
        (b.last_contact_at ?? "").localeCompare(a.last_contact_at ?? "") ||
        (b.last_enquiry_at ?? "").localeCompare(a.last_enquiry_at ?? "") ||
        b.created_at.localeCompare(a.created_at) || a.id.localeCompare(b.id)
      );
    const page = rows.slice(
      query.page * PAGE_SIZE,
      (query.page + 1) * PAGE_SIZE,
    );
    return {
      rows: page.map((l) => ({
        ...l,
        next_task: this.db.tasks.find((t) => t.lead_id === l.id && t.status === "open") ?? null,
      })),
      total: rows.length,
    };
  }

  async lead(id: string): Promise<LeadDetail> {
    const lead = this.visibleLead(id);
    if (!lead) {
      throw new ApiError(
        "Not found, or you do not have access to it.",
        "not_found",
      );
    }
    const by = <
      T extends { lead_id: string | null; member_id?: string | null },
    >(rows: T[]) => this.mine(rows as (T & { member_id: string | null })[]).filter((r) => r.lead_id === id);
    const recIds = new Set(by(this.db.recordings).map((r) => r.id));
    return {
      lead: { ...lead },
      calls: by(this.db.calls).sort((a, b) => b.started_at.localeCompare(a.started_at)),
      recordings: by(this.db.recordings),
      analyses: this.db.analyses.filter((a) => recIds.has(a.recording_id)).sort(
        (a, b) => b.created_at.localeCompare(a.created_at),
      ),
      tasks: by(this.db.tasks).sort((a, b) => b.due_at.localeCompare(a.due_at)),
      insights: by(this.db.insights),
    };
  }

  async updateLead(
    id: string,
    patch: {
      stage?: Stage;
      owner_member_id?: string | null;
      name?: string | null;
    },
  ) {
    const lead = this.visibleLead(id);
    if (!lead) {
      throw new ApiError(
        "Not found, or you do not have access to it.",
        "not_found",
      );
    }
    if (patch.owner_member_id !== undefined && !this.isManager()) {
      throw new ApiError(
        "Only owners, admins and managers can reassign leads.",
        "42501",
      );
    }
    // The leads_update rule: the customer's owner or a manager. A colleague's customer she sees through a follow-up of
    // hers (D-163) is not hers to change; the database changes no row, which the real API reports so.
    if (!this.isManager() && lead.owner_member_id !== this.me().id) {
      throw new ApiError("You do not have permission to change that.", "42501");
    }
    const { owner_member_id: owner, ...rest } = patch;
    if (owner !== undefined) this.setOwner(lead, owner);
    const from = lead.stage;
    const now = new Date().toISOString();
    Object.assign(lead, rest, { updated_at: now });
    if (lead.stage !== from) {
      // As the leads_timeline trigger keeps it (D-163's history).
      const status = statusOf(lead.stage);
      if (statusOf(from) !== status) this.crm.closedAt.set(lead.id, status === "open" ? null : now);
      const stageId = stageOf(lead.stage).id;
      this.logEvent(lead.id, "lead.stage", { from, to: lead.stage, stage_id: stageId, lost_reason: null }, now);
    }
  }

  // Mirrors erase_lead(): the customer goes at once; recording rows stay, stripped, for billing (PRIV-01).
  async eraseLead(id: string, reason: string, neverRecord: boolean): Promise<EraseResult> {
    this.requireAdmin();
    if (!reason.trim()) {
      throw new ApiError("Give a reason, such as the customer's request.", "22023");
    }
    const lead = this.db.leads.find((l) => l.id === id);
    if (!lead) throw new ApiError("No such lead.", "P0002");
    const phone = lead.phone_e164;
    const calls = new Set(this.db.calls.filter((c) => c.lead_id === id || c.phone_e164 === phone).map((c) => c.id));
    const recs = this.db.recordings.filter((r) =>
      r.lead_id === id || r.phone_hint === phone || (r.call_id !== null && calls.has(r.call_id))
    );
    const recIds = new Set(recs.map((r) => r.id));
    const now = new Date().toISOString();
    for (const r of recs) {
      Object.assign(r, {
        erase_requested_at: now,
        lead_id: null,
        call_id: null,
        phone_hint: null,
        file_name: "[erased]",
      });
    }
    const followUps = this.db.tasks.filter((t) => t.lead_id === id).length;
    const analyses = this.db.analyses.filter((a) => a.lead_id === id || recIds.has(a.recording_id)).length;
    this.db.dispositions = this.db.dispositions.filter((d) =>
      d.lead_id !== id && !(d.recording_id && recIds.has(d.recording_id))
    );
    this.db.tasks = this.db.tasks.filter((t) => t.lead_id !== id);
    this.db.insights = this.db.insights.filter((i) => i.lead_id !== id && !recIds.has(i.recording_id));
    this.db.analyses = this.db.analyses.filter((a) => a.lead_id !== id && !recIds.has(a.recording_id));
    this.db.transcripts = this.db.transcripts.filter((t) => !recIds.has(t.recording_id));
    this.db.calls = this.db.calls.filter((c) => !calls.has(c.id));
    this.db.leads = this.db.leads.filter((l) => l.id !== id);
    this.crm.numbers = this.crm.numbers.filter((n) => n.lead_id !== id);
    this.crm.contacts = this.crm.contacts.filter((c) => c.lead_id !== id);
    this.crm.events = this.crm.events.filter((e) => e.lead_id !== id);
    this.crm.labels.delete(id);
    if (neverRecord && !this.db.excluded.some((x) => x.phone_e164 === phone)) {
      this.db.excluded.unshift({
        id: newId(),
        org_id: this.db.org.id,
        phone_e164: phone,
        reason: "Erased on request",
        added_by: this.me().id,
        created_at: now,
      });
    }
    return { calls: calls.size, recordings: recs.length, follow_ups: followUps, analyses };
  }

  async addLead(
    input: { phone: string; name?: string; ownerMemberId?: string },
  ) {
    const phone = e164(input.phone);
    if (!phone) {
      throw new ApiError("That is not a valid phone number.", "22023");
    }
    const existing = this.leadForNumber(phone);
    if (existing) return existing.id;
    const now = new Date().toISOString();
    const id = newId();
    this.db.leads.push({
      id,
      org_id: this.db.org.id,
      phone_e164: phone,
      name: input.name || null,
      owner_member_id: input.ownerMemberId || this.me().id,
      stage: "new",
      interest: null,
      summary: null,
      facts: {},
      source: "manual",
      first_contact_at: null,
      last_contact_at: null,
      last_enquiry_at: null,
      source_id: null,
      campaign: null,
      created_at: now,
      updated_at: now,
    });
    return id;
  }

  // ------------------------------------------------- the CRM, as on the phone (D-163), as the crm_* functions do

  private ref(id: string | null | undefined): Ref | null {
    const m = id ? this.db.members.find((x) => x.id === id) : undefined;
    return m ? { id: m.id, name: m.display_name } : null;
  }

  private activeMember(id: string | null | undefined) {
    return id ? this.db.members.find((m) => m.id === id && m.status === "active") ?? null : null;
  }

  /** Now, and where today, tomorrow and the next 7 days end in the company's time zone. */
  private days(at = new Date()) {
    const tz = this.db.org.timezone || DEMO_TZ;
    const today = localDate(at, tz);
    const start = (n: number) => fromWallTime(`${addDay(today, n)}T00:00`, tz).getTime();
    return { now: at.getTime(), dayStart: start(0), dayEnd: start(1), tomorrowEnd: start(2), weekEnd: start(7) };
  }

  private openFollowUps(leadId: string): DemoTask[] {
    return this.db.tasks
      .filter((t) => t.lead_id === leadId && t.status === "open")
      .sort((a, b) => Date.parse(a.due_at) - Date.parse(b.due_at) || a.created_at.localeCompare(b.created_at));
  }

  /** The customer's numbers: the main one (the customer's own) first, then those added on the page. */
  private numbersOf(l: Lead): DemoNumber[] {
    return [
      {
        id: `number-${l.id}`,
        lead_id: l.id,
        e164: l.phone_e164,
        label: null,
        is_primary: true,
        is_whatsapp: true,
        contact_id: `contact-${l.id}`,
        created_at: l.created_at,
      },
      ...this.crm.numbers.filter((n) => n.lead_id === l.id),
    ];
  }

  private contactsOf(l: Lead): DemoContact[] {
    return [
      {
        id: `contact-${l.id}`,
        lead_id: l.id,
        name: l.name?.trim() || null,
        role: null,
        is_primary: true,
        created_at: l.created_at,
      },
      ...this.crm.contacts.filter((c) => c.lead_id === l.id),
    ];
  }

  /** The customer a number belongs to (private.lead_for_number): their main number or one added to them. */
  private leadForNumber(phone: string): Lead | null {
    return this.db.leads.find((l) => l.phone_e164 === phone) ??
      this.db.leads.find((l) => this.crm.numbers.some((n) => n.lead_id === l.id && n.e164 === phone)) ?? null;
  }

  /** What the database keeps worked out on the lead: call counts, contact dates, the next follow-up. */
  private crmFacts(l: Lead): CrmFacts {
    const calls = this.db.calls.filter((c) => c.lead_id === l.id);
    const connected = calls.filter(connectedCall).map((c) => c.started_at).sort();
    const open = this.openFollowUps(l.id);
    return {
      calls: calls.length,
      connected: connected.length,
      lastConnected: connected[connected.length - 1] ?? null,
      lastContact: l.last_contact_at ?? calls.map((c) => c.started_at).sort()[calls.length - 1] ?? null,
      open,
      next: open[0] ?? null,
      status: statusOf(l.stage),
      label: this.crm.labels.get(l.id)?.label ?? null,
      numbers: this.numbersOf(l),
      assignedAt: this.crm.assignedAt.has(l.id)
        ? this.crm.assignedAt.get(l.id) ?? null
        : l.owner_member_id
        ? l.created_at
        : null,
    };
  }

  /** When a won or lost customer was closed: when it moved there, else (the demo's own) their last call. */
  private closedAt(l: Lead): string | null {
    if (statusOf(l.stage) === "open") return null;
    return this.crm.closedAt.get(l.id) ?? l.last_contact_at ?? l.updated_at;
  }

  private customerRow(l: Lead, f: CrmFacts, now: number): CustomerRow {
    const source = this.db.sources.find((s) => s.id === l.source_id);
    const next = f.next;
    return {
      id: l.id,
      name: displayName(l),
      has_name: !!l.name?.trim(),
      phone: l.phone_e164,
      label: f.label,
      interest: l.interest ?? "none",
      stage: { ...stageOf(l.stage) },
      status: f.status,
      owner: this.ref(l.owner_member_id),
      source: source ? { id: source.id, name: source.name, type: source.type } : null,
      origin: l.source,
      campaign: l.campaign,
      context: contextOf(l.facts),
      city: null,
      next_follow_up: next
        ? {
          id: next.id,
          type: next.type as FollowUpType,
          kind: followUpKind(next.type),
          due_at: next.due_at,
          due_has_time: next.due_has_time ?? true,
          overdue: Date.parse(next.due_at) < now,
          member: this.ref(next.member_id),
        }
        : null,
      open_follow_ups: f.open.length,
      last_contact_at: f.lastContact,
      last_connected_at: f.lastConnected,
      calls: f.calls,
      connected_calls: f.connected,
      created_at: l.created_at,
      enquired_at: l.last_enquiry_at,
      assigned_at: f.assignedAt,
      expected_value: null,
    };
  }

  async customerList(query: CustomerQuery): Promise<CustomerList> {
    const me = this.me();
    const manager = this.isManager();
    const v = (s: string | undefined) => s?.trim() || null;
    const [stage, status, label, ai, owner, source] = [
      query.stage,
      query.status,
      query.label,
      query.ai,
      query.owner,
      query.source,
    ].map(v);
    const [follow, contact, added] = [query.follow_up, query.contact, query.added].map(v);
    const sort = v(query.sort) ?? "next";
    const check = (value: string | null, allowed: string[], message: string) => {
      if (value !== null && !allowed.includes(value)) throw new ApiError(message, "22023");
    };
    check(status, ["open", "won", "lost", "closed"], "Status is open, won, lost or closed.");
    check(label, ["hot", "warm", "cold", "none"], "Label is hot, warm, cold or none.");
    check(ai, ["hot", "warm", "cold"], "The AI's reading is hot, warm or cold.");
    check(follow, ["overdue", "today", "upcoming", "none"], "Follow-up is overdue, today, upcoming or none.");
    check(contact, ["never", "stale", "week"], "Contact is never, stale or week.");
    check(added, ["today", "week", "month"], "Added is today, week or month.");
    check(sort, ["next", "recent", "newest", "name"], "Sort is next, recent, newest or name.");
    const ownerId = owner === "me" ? me.id : owner;
    const { now, dayStart, dayEnd } = this.days();
    const text = (query.q ?? "").trim().toLowerCase();
    const digits = (query.q ?? "").replace(/\D/g, "");
    const weekAgo = now - 7 * 86_400_000;
    const addedFrom = dayStart - (added === "week" ? 6 : added === "month" ? 29 : 0) * 86_400_000;

    // The filters, as crm_customers() has them.
    const byFollowUp = (f: CrmFacts) => {
      const due = ms(f.next?.due_at);
      switch (follow) {
        case null:
          return true;
        case "overdue":
          return due !== null && due < now;
        case "today":
          return due !== null && due >= now && due < dayEnd;
        case "upcoming":
          return due !== null && due >= dayEnd;
        default:
          return due === null && f.status === "open";
      }
    };
    const byContact = (f: CrmFacts) => {
      const last = ms(f.lastContact);
      switch (contact) {
        case null:
          return true;
        case "never":
          return f.calls === 0;
        case "stale":
          return (last ?? -Infinity) < weekAgo;
        default:
          return last !== null && last >= weekAgo;
      }
    };
    const byStatus = (f: CrmFacts) => {
      switch (status) {
        case null:
          return true;
        case "closed":
          return f.status !== "open";
        case "lost":
          return f.status === "lost" || f.status === "not_a_lead";
        default:
          return f.status === status;
      }
    };
    const found = (l: Lead, f: CrmFacts) =>
      !text || (l.name ?? "").toLowerCase().includes(text) ||
      (digits.length >= 3 && f.numbers.some((n) => n.e164.includes(digits)));

    const base = this.visibleLeads().map((l) => ({ l, f: this.crmFacts(l) })).filter(({ l, f }) =>
      found(l, f) &&
      (!label || (label === "none" ? f.label === null : f.label === label)) &&
      (!ai || l.interest === ai) &&
      byFollowUp(f) &&
      byContact(f) &&
      (!added || Date.parse(l.last_enquiry_at ?? l.created_at) >= addedFrom)
    ).map(({ l, f }) => ({
      l,
      f,
      // The counts beside each filter leave that filter out: per stage whatever stage is chosen, and so on.
      byStage: (!stage || stageOf(l.stage).id === stage) && byStatus(f),
      byOwner: !owner || (owner === "none" ? !l.owner_member_id : l.owner_member_id === ownerId),
      bySource: !source || (source === "none" ? !l.source_id : l.source_id === source),
    }));

    const newest = (l: Lead) => Date.parse(l.last_enquiry_at ?? l.created_at);
    const chosen = base.filter((b) => b.byStage && b.byOwner && b.bySource).sort((a, b) =>
      (sort === "next" ? Number(a.f.status !== "open") - Number(b.f.status !== "open") : 0) ||
      (sort === "next" ? nullsLast(ms(a.f.next?.due_at), ms(b.f.next?.due_at), 1) : 0) ||
      (sort === "next" || sort === "recent" ? nullsLast(ms(a.f.lastContact), ms(b.f.lastContact), -1) : 0) ||
      (sort === "name" ? displayName(a.l).toLowerCase().localeCompare(displayName(b.l).toLowerCase()) : 0) ||
      newest(b.l) - newest(a.l) || a.l.id.localeCompare(b.l.id)
    );
    const group = (rows: typeof base, key: (l: Lead) => string | null, name: (id: string) => string | null) => {
      const counts = new Map<string | null, number>();
      for (const b of rows) counts.set(key(b.l), (counts.get(key(b.l)) ?? 0) + 1);
      return [...counts.entries()].map(([id, count]) => ({ id, name: id ? name(id) : null, count }));
    };
    const offset = Math.max(0, query.page) * CUSTOMERS_PAGE;
    return {
      total: chosen.length,
      rows: chosen.slice(offset, offset + CUSTOMERS_PAGE).map((b) => this.customerRow(b.l, b.f, now)),
      stages: DEMO_STAGES.map((s) => ({
        ...s,
        count: base.filter((b) => b.l.stage === s.key && b.byOwner && b.bySource).length,
      })),
      owners: manager
        ? group(
          base.filter((b) => b.byStage && b.bySource),
          (l) => l.owner_member_id,
          (id) => this.ref(id)?.name ?? null,
        )
          .sort((a, b) => Number(a.id === null) - Number(b.id === null) || (a.name ?? "").localeCompare(b.name ?? ""))
        : null,
      sources: group(
        base.filter((b) => b.byStage && b.byOwner),
        (l) => l.source_id,
        (id) => this.db.sources.find((s) => s.id === id)?.name ?? null,
      ).sort((a, b) => Number(a.id === null) - Number(b.id === null) || b.count - a.count),
    };
  }

  private followUpCard(t: DemoTask, now: number, dayStart: number): CustomerFollowUp {
    const due = Date.parse(t.due_at);
    const hasTime = t.due_has_time ?? true;
    return {
      id: t.id,
      type: t.type as FollowUpType,
      due_at: t.due_at,
      due_has_time: hasTime,
      overdue: hasTime ? due < now : due < dayStart,
      reason: t.reason,
      brief: t.brief,
      source: t.source,
      rule: t.source_rule?.split(":")[0] || null,
      place: t.place ?? null,
      assigned_to: t.member_id,
      booked_by: t.booked_by ?? null,
    };
  }

  async customerPage(id: string): Promise<CustomerPage> {
    const l = this.visibleLead(id);
    if (!l) throw new ApiError("No such customer.", "P0002");
    const f = this.crmFacts(l);
    const { now, dayStart } = this.days();
    const label = this.crm.labels.get(l.id);
    const stage = stageOf(l.stage);
    const facts = l.facts && typeof l.facts === "object" && !Array.isArray(l.facts) ? { ...l.facts } : {};
    return {
      customer: {
        id: l.id,
        name: displayName(l),
        has_name: !!l.name?.trim(),
        phone: l.phone_e164,
        label: label?.label ?? null,
        score: null,
        context: contextOf(l.facts),
        stage: stage.name,
        stage_id: stage.id,
        status: f.status,
        next_follow_up_at: f.next?.due_at ?? null,
        last_contact_at: f.lastContact,
        calls: f.calls,
        connected_calls: f.connected,
        last_connected_at: f.lastConnected,
        enquired_at: l.last_enquiry_at,
        assigned_at: f.assignedAt,
        created_at: l.created_at,
        owner: this.ref(l.owner_member_id),
        source: this.db.sources.find((s) => s.id === l.source_id)?.name ?? null,
        details: facts,
        expected_value: null,
        address: null,
        city: null,
        lost_reason: stage.kind === "lost" ? this.crm.lostReasons.get(l.id) ?? null : null,
        label_set_at: label?.at ?? null,
        summary: l.summary,
      },
      numbers: f.numbers.map((n) => ({
        id: n.id,
        e164: n.e164,
        label: n.label,
        is_primary: n.is_primary,
        is_whatsapp: n.is_whatsapp,
        contact_id: n.contact_id,
      })),
      contacts: this.contactsOf(l).map((c) => ({ id: c.id, name: c.name, role: c.role, is_primary: c.is_primary })),
      follow_ups: f.open.map((t) => this.followUpCard(t, now, dayStart)),
      timeline: this.timeline(l),
      stages: DEMO_STAGES.map((s) => ({ ...s })),
    };
  }

  /**
   * The customer's history as the phone shows it, newest first and at most 100: calls, follow-ups made and closed,
   * and what was done on the customer (stage, label, owner, notes, messages). Another telecaller's recordings stay
   * hers (LEAD-10).
   */
  private timeline(l: Lead): TimelineEvent[] {
    const me = this.me();
    const manager = this.isManager();
    const name = (id: string | null | undefined) => this.ref(id)?.name ?? null;
    const out: (TimelineEvent & { seq: number })[] = [{
      id: `created-${l.id}`,
      type: "lead.created",
      at: l.created_at,
      actor: "system",
      by: null,
      payload: { via: l.source, source_id: l.source_id, owner: l.owner_member_id },
      call: null,
      seq: 0,
    }];
    for (const c of this.db.calls.filter((x) => x.lead_id === l.id)) {
      const rec = this.db.recordings.find((r) => r.call_id === c.id && (manager || r.member_id === me.id));
      out.push({
        id: `call-${c.id}`,
        type: "call.logged",
        at: c.started_at,
        actor: "member",
        by: name(c.member_id),
        payload: {
          interaction_id: c.id,
          direction: c.direction,
          call_status: c.call_status,
          duration_s: c.duration_s,
          outcome: c.outcome,
        },
        call: {
          id: c.id,
          outcome: c.outcome,
          ...(rec
            ? {
              recording: {
                id: rec.id,
                status: rec.status,
                duration_s: rec.duration_s,
                playable: !rec.audio_deleted_at && !rec.erase_requested_at,
                summary: this.db.analyses.find((a) => a.recording_id === rec.id)?.summary ?? null,
              },
            }
            : {}),
        },
        seq: 1,
      });
    }
    for (const t of this.db.tasks.filter((x) => x.lead_id === l.id) as DemoTask[]) {
      const actor: TimelineEvent["actor"] = t.source === "ai"
        ? "ai"
        : t.source === "rule"
        ? "rule"
        : t.created_by_member_id
        ? "member"
        : "system";
      out.push({
        id: `task-${t.id}`,
        type: "followup.created",
        at: t.created_at,
        actor,
        by: actor === "member" ? name(t.created_by_member_id) : null,
        payload: {
          task_id: t.id,
          type: t.type,
          due_at: t.due_at,
          source: t.source,
          rule: t.source_rule,
          assigned_to: t.member_id,
          reason: t.reason,
        },
        call: null,
        seq: 1,
      });
      if (t.status === "open") continue;
      const closed = {
        at: t.completed_at ?? t.updated_at,
        actor: (t.closed_by ? "member" : "system") as TimelineEvent["actor"],
        by: name(t.closed_by),
        call: null,
      };
      out.push({
        id: `task-${t.id}-${t.status}`,
        type: `followup.${t.status}`,
        ...closed,
        payload: { task_id: t.id, type: t.type, result: t.result ?? null },
        seq: 2,
      });
      if (t.result) {
        out.push({
          id: `task-${t.id}-result`,
          type: "followup.result",
          ...closed,
          payload: { task_id: t.id, type: t.type, result: t.result },
          seq: 3,
        });
      }
    }
    for (const e of this.crm.events) {
      if (e.lead_id !== l.id) continue;
      out.push({
        id: e.id,
        type: e.type,
        at: e.at,
        actor: e.actor,
        by: e.by,
        payload: e.payload,
        call: e.call,
        seq: e.seq,
      });
    }
    return out
      .sort((a, b) => Date.parse(b.at) - Date.parse(a.at) || b.seq - a.seq)
      .slice(0, 100)
      .map((e) => ({ id: e.id, type: e.type, at: e.at, actor: e.actor, by: e.by, payload: e.payload, call: e.call }));
  }

  /** Something done on a customer, kept in their history as the database's triggers keep it. */
  private logEvent(leadId: string, type: string, payload: Record<string, unknown>, at = new Date().toISOString()) {
    const me = this.me();
    this.crm.events.push({
      id: newId(),
      lead_id: leadId,
      type,
      at,
      actor: "member",
      by: me.display_name,
      payload,
      call: null,
      seq: 100 + ++this.crm.seq,
    });
  }

  private followUpRow(t: DemoTask, bucket: Bucket | null, now: number): FollowUpRow {
    const l = this.db.leads.find((x) => x.id === t.lead_id)!;
    return {
      id: t.id,
      type: t.type as FollowUpType,
      kind: followUpKind(t.type),
      status: t.status,
      due_at: t.due_at,
      due_has_time: t.due_has_time ?? true,
      bucket,
      overdue: t.status === "open" && Date.parse(t.due_at) < now,
      reason: t.reason,
      brief: t.brief,
      source: t.source,
      rule: t.source_rule,
      place: t.place ?? null,
      result: t.result ?? null,
      completed_at: t.completed_at,
      snoozed_count: t.snoozed_count,
      created_at: t.created_at,
      member: this.ref(t.member_id),
      booked_by: this.ref(t.booked_by),
      created_by: this.ref(t.created_by_member_id),
      lead: {
        id: l.id,
        name: displayName(l),
        phone: l.phone_e164,
        label: this.crm.labels.get(l.id)?.label ?? null,
        interest: l.interest ?? "none",
        status: statusOf(l.stage),
        context: contextOf(l.facts),
        stage: stageOf(l.stage).name,
        owner: this.ref(l.owner_member_id),
      },
    };
  }

  async followUps(query: FollowUpQuery): Promise<FollowUpList> {
    const me = this.me();
    const manager = this.isManager();
    const who = query.who || (manager ? "all" : "me");
    if (!manager && who !== "me") {
      throw new ApiError("Only owners, admins and managers see the team's follow-ups.", "42501");
    }
    const kind = query.kind || null;
    const due = query.due || null;
    const state = query.state || "open";
    if (kind && !FOLLOW_UP_KINDS.some((k) => k.kind === kind)) {
      throw new ApiError("Kind is call, visit, meeting, send or collect.", "22023");
    }
    if (due && !["overdue", "today", "tomorrow", "week", "later"].includes(due)) {
      throw new ApiError("Due is overdue, today, tomorrow, week or later.", "22023");
    }
    if (state !== "open" && state !== "done") throw new ApiError("State is open or done.", "22023");
    const { now, dayStart, dayEnd, tomorrowEnd, weekEnd } = this.days();
    const weekStart = dayStart - 6 * 86_400_000;
    const forWho = (t: Task) => (who === "me" ? t.member_id === me.id : who === "all" || t.member_id === who);
    const text = (query.q ?? "").trim().toLowerCase();
    const digits = (query.q ?? "").replace(/\D/g, "");
    const leads = new Map(this.db.leads.map((l) => [l.id, l]));
    const doneThisWeek = (t: Task) =>
      t.status === "done" && !!t.completed_at && Date.parse(t.completed_at) >= weekStart;
    const bucketOf = (t: Task): Bucket | null => {
      if (t.status !== "open") return null;
      const d = Date.parse(t.due_at);
      return d < now ? "overdue" : d < dayEnd ? "today" : d < tomorrowEnd ? "tomorrow" : d < weekEnd ? "week" : "later";
    };
    const tasks = this.db.tasks as DemoTask[];
    const dated = tasks.filter((t) => {
      const l = leads.get(t.lead_id);
      return !!l && forWho(t) && (!kind || followUpKind(t.type) === kind) &&
        (state === "open" ? t.status === "open" : doneThisWeek(t)) &&
        (!text || (l.name ?? "").toLowerCase().includes(text) || (digits.length >= 3 && l.phone_e164.includes(digits)));
    }).map((t) => ({ t, bucket: bucketOf(t) }));
    const chosen = dated.filter((d) => !due || d.bucket === due).sort((a, b) =>
      (state === "open"
        ? Date.parse(a.t.due_at) - Date.parse(b.t.due_at)
        : Date.parse(b.t.completed_at!) - Date.parse(a.t.completed_at!)) || a.t.id.localeCompare(b.t.id)
    );
    const open = tasks.filter((t) => t.status === "open" && forWho(t));
    const offset = Math.max(0, query.page) * FOLLOW_UPS_PAGE;
    const count = (b: Bucket) => dated.filter((d) => d.bucket === b).length;
    const people: FollowUpPerson[] = this.db.members
      .filter((m) => m.status === "active" && (manager || m.id === me.id))
      .map((m) => ({ m, ts: tasks.filter((t) => t.member_id === m.id && (t.status === "open" || doneThisWeek(t))) }))
      .filter(({ m, ts }) => m.role === "caller" || ts.length > 0)
      .map(({ m, ts }) => {
        const open = ts.filter((t) => t.status === "open");
        const done = ts.filter((t) => t.status === "done");
        return {
          id: m.id,
          name: m.display_name,
          role: m.role,
          overdue: open.filter((t) => Date.parse(t.due_at) < now).length,
          today: open.filter((t) => Date.parse(t.due_at) >= now && Date.parse(t.due_at) < dayEnd).length,
          upcoming: open.filter((t) => Date.parse(t.due_at) >= dayEnd).length,
          visits: open.filter((t) => isVisit(t.type)).length,
          done_today: done.filter((t) => Date.parse(t.completed_at!) >= dayStart).length,
          done_week: done.length,
          on_time_week: done.filter((t) => Date.parse(t.completed_at!) <= Date.parse(t.due_at) + 15 * 60_000).length,
        };
      })
      .sort((a, b) => b.overdue - a.overdue || a.name.localeCompare(b.name));
    return {
      total: chosen.length,
      rows: chosen.slice(offset, offset + FOLLOW_UPS_PAGE).map((d) => this.followUpRow(d.t, d.bucket, now)),
      buckets: {
        overdue: count("overdue"),
        today: count("today"),
        tomorrow: count("tomorrow"),
        week: count("week"),
        later: count("later"),
      },
      kinds: Object.fromEntries(
        FOLLOW_UP_KINDS.map((k) => [k.kind, open.filter((t) => followUpKind(t.type) === k.kind).length]),
      ) as Record<FollowUpKind, number>,
      people,
    };
  }

  async insights(from: string, to: string): Promise<Insights> {
    const me = this.me();
    const manager = this.isManager();
    const start = Date.parse(from);
    const end = Date.parse(to);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || end - start > 366 * 86_400_000) {
      throw new ApiError("Choose a period of up to a year.", "22023");
    }
    const now = Date.now();
    const within = (iso: string | null | undefined) => {
      const t = ms(iso);
      return t !== null && t >= start && t < end;
    };
    // Managers count the whole company; a telecaller her own customers and follow-ups.
    const scope = this.db.leads.filter((l) => manager || l.owner_member_id === me.id);
    const facts = new Map(scope.map((l) => [l.id, this.crmFacts(l)]));
    const inScope = new Set(scope.map((l) => l.id));
    const fresh = scope.filter((l) => within(l.created_at));
    const tasks = this.db.tasks as DemoTask[];
    const closedIn = (l: Lead, statuses: CustomerStatus[]) =>
      statuses.includes(statusOf(l.stage)) && within(this.closedAt(l));
    const reasons = new Map<string, number>();
    for (const l of scope.filter((x) => closedIn(x, ["lost", "not_a_lead"]))) {
      const reason = this.crm.lostReasons.get(l.id)?.trim() || "No reason given";
      reasons.set(reason, (reasons.get(reason) ?? 0) + 1);
    }
    // Customers without a source are named by how they came in, as the database does; the demo's portal leads by
    // the portal.
    const originName = (o: string) =>
      ({ call_log: "Calls", import: "Imported", manual: "Added by hand", integration: "Integration" } as Record<
        string,
        string
      >)[o] ?? o;
    const groups = new Map<string, { id: string | null; name: string; leads: Lead[] }>();
    for (const l of fresh) {
      const s = this.db.sources.find((x) => x.id === l.source_id);
      const name = s?.name ?? originName(l.source);
      const key = `${s?.id ?? ""}|${name}`;
      if (!groups.has(key)) groups.set(key, { id: s?.id ?? null, name, leads: [] });
      groups.get(key)!.leads.push(l);
    }
    const label = (l: Lead) => facts.get(l.id)!.label;
    const won = (l: Lead) => statusOf(l.stage) === "won";
    // Enquiries that arrive by themselves (integrations, and the demo's portal leads): how fast they were called.
    const arrived = fresh.filter((l) => !["call_log", "import", "manual"].includes(l.source));
    const waited = (l: Lead, minutes: number) =>
      !!l.first_contact_at && Date.parse(l.first_contact_at) <= Date.parse(l.created_at) + minutes * 60_000;
    const owned = (m: string) => scope.filter((l) => l.owner_member_id === m);
    const openOf = (m: string) => owned(m).filter((l) => statusOf(l.stage) === "open");
    const doneBy = (m: string) =>
      tasks.filter((t) => t.member_id === m && t.status === "done" && within(t.completed_at));
    return {
      from: new Date(start).toISOString(),
      to: new Date(end).toISOString(),
      pipeline: DEMO_STAGES.map((st) => {
        const ls = scope.filter((l) => l.stage === st.key);
        return {
          ...st,
          count: ls.length,
          hot: ls.filter((l) => label(l) === "hot").length,
          warm: ls.filter((l) => label(l) === "warm").length,
          value: null,
        };
      }),
      unassigned: manager ? scope.filter((l) => !l.owner_member_id && statusOf(l.stage) === "open").length : null,
      moved: {
        new: fresh.length,
        contacted: scope.filter((l) => within(l.first_contact_at)).length,
        visits_booked: tasks.filter((t) =>
          inScope.has(t.lead_id) && isVisit(t.type) && within(t.created_at) &&
          ["caller", "owner", "manual"].includes(t.source)
        ).length,
        visits_done: tasks.filter((t) => inScope.has(t.lead_id) && t.result === "happened" && within(t.completed_at))
          .length,
        won: scope.filter((l) => closedIn(l, ["won"])).length,
        won_value: null,
        lost: scope.filter((l) => closedIn(l, ["lost", "not_a_lead"])).length,
      },
      lost_reasons: [...reasons.entries()]
        .map(([reason, count]) => ({ reason, count }))
        .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason))
        .slice(0, 8),
      sources: [...groups.values()].map((g) => ({
        id: g.id,
        name: g.name,
        leads: g.leads.length,
        called: g.leads.filter((l) => facts.get(l.id)!.calls > 0).length,
        connected: g.leads.filter((l) => facts.get(l.id)!.connected > 0).length,
        interested: g.leads.filter((l) =>
          label(l) === "hot" || label(l) === "warm" || won(l) ||
          ["interested", "site_visit", "negotiating"].includes(l.stage)
        ).length,
        visit: g.leads.filter((l) =>
          ["site_visit", "negotiating"].includes(l.stage) || won(l) ||
          tasks.some((t) => t.lead_id === l.id && isVisit(t.type))
        ).length,
        won: g.leads.filter(won).length,
      })).sort((a, b) => b.leads - a.leads || a.name.localeCompare(b.name)),
      speed: {
        leads: arrived.length,
        called: arrived.filter((l) => l.first_contact_at).length,
        within_5m: arrived.filter((l) => waited(l, 5)).length,
        within_1h: arrived.filter((l) => waited(l, 60)).length,
        median_minutes: median(
          arrived
            .filter((l) => l.first_contact_at && Date.parse(l.first_contact_at) >= Date.parse(l.created_at))
            .map((l) => (Date.parse(l.first_contact_at!) - Date.parse(l.created_at)) / 60_000),
        ),
      },
      people: this.db.members
        .filter((m) =>
          m.status === "active" && (manager || m.id === me.id) && (m.role === "caller" || owned(m.id).length > 0)
        )
        .map((m) => ({
          id: m.id,
          name: m.display_name,
          role: m.role,
          open: openOf(m.id).length,
          untouched: openOf(m.id).filter((l) => facts.get(l.id)!.calls === 0).length,
          no_next: openOf(m.id).filter((l) => facts.get(l.id)!.calls > 0 && !facts.get(l.id)!.next).length,
          overdue: tasks.filter((t) => t.member_id === m.id && t.status === "open" && Date.parse(t.due_at) < now)
            .length,
          done: doneBy(m.id).length,
          on_time: doneBy(m.id).filter((t) => Date.parse(t.completed_at!) <= Date.parse(t.due_at) + 15 * 60_000)
            .length,
          visits_booked: tasks.filter((t) => t.booked_by === m.id && within(t.created_at)).length,
          won: owned(m.id).filter((l) => closedIn(l, ["won"])).length,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    };
  }

  /** private.task_for_actor(): her own follow-up, or anyone's for owners, admins and managers. */
  private taskForActor(id: string): DemoTask {
    const me = this.me();
    const t = this.db.tasks.find((x) => x.id === id && (x.member_id === me.id || this.isManager()));
    if (!t) throw new ApiError("No such follow-up.", "P0002");
    return t;
  }

  /** A change to an open follow-up, kept in the customer's history when it moves or changes hands. */
  private changeTask(t: DemoTask, patch: Partial<DemoTask>, snoozed = false) {
    const before = { due: t.due_at, member: t.member_id };
    Object.assign(t, patch, { updated_at: new Date().toISOString() });
    if (t.due_at !== before.due) {
      this.logEvent(t.lead_id, "followup.moved", {
        task_id: t.id,
        type: t.type,
        from: before.due,
        to: t.due_at,
        snoozed,
      });
    }
    if (t.member_id !== before.member) {
      this.logEvent(t.lead_id, "followup.reassigned", { task_id: t.id, from: before.member, to: t.member_id });
    }
  }

  private closeTask(
    t: DemoTask,
    status: "done" | "cancelled",
    result: VisitResult | null = null,
    at = new Date().toISOString(),
  ) {
    Object.assign(t, { status, completed_at: at, updated_at: at, result, closed_by: this.me().id });
  }

  async saveFollowUp(input: FollowUpInput): Promise<void> {
    const me = this.me();
    const manager = this.isManager();
    const source = (manager ? "owner" : "caller") as Task["source"];
    const kind = input.type || "call";
    if (!FOLLOW_UP_KINDS.some((k) => k.kind === kind)) {
      throw new ApiError("A follow-up is a call, visit, meeting, send or collect.", "22023");
    }
    const type = kind as TaskType;
    const nowMs = Date.now();
    const due = Date.parse(input.due_at);
    if (!Number.isFinite(due) || due < nowMs - 86_400_000 || due > nowMs + 366 * 86_400_000) {
      throw new ApiError("Pick a time from now to a year ahead.", "22023");
    }
    const dueAt = new Date(due).toISOString();
    const hasTime = input.due_has_time ?? true;
    const place = input.place?.trim() || null;
    const reason = input.reason?.trim() || REASONS[kind];
    const tasks = this.db.tasks as DemoTask[];

    // A change to a follow-up: hers, one she made, or anyone's for a manager.
    const existing = input.id ? tasks.find((t) => t.id === input.id) : undefined;
    if (existing) {
      if (existing.member_id !== me.id && existing.created_by_member_id !== me.id && !manager) {
        throw new ApiError("A colleague has this follow-up; ask your manager to change it.", "42501");
      }
      if (existing.status !== "open") throw new ApiError("This follow-up is already closed.", "22023");
      const byVoxowa = ["ai", "rule", "system"].includes(existing.source);
      this.changeTask(existing, {
        type,
        due_at: dueAt,
        due_has_time: hasTime,
        member_id: this.activeMember(input.assigned_to)?.id ?? existing.member_id,
        place: place ?? existing.place ?? null,
        ...(byVoxowa ? { source, created_by_member_id: me.id } : {}),
      });
      return;
    }

    const lead = input.lead_id ? this.visibleLead(input.lead_id) : null;
    if (!lead) throw new ApiError("No such customer.", "P0002");
    // A new visit given to nobody goes to whoever handles the company's visits, at its place (D-186).
    const assignee = this.activeMember(input.assigned_to)?.id ??
      (kind === "visit" ? this.visitHandler(me.id, dueAt) : me.id);
    const visitPlace = kind === "visit" ? (place ?? this.visitPublic().place) : place;
    // A call (or anything but a visit or meeting) takes the place of the customer's one open follow-up (TASK-01).
    if (!BESIDE.includes(type)) {
      const holder = tasks.find((t) => t.lead_id === lead.id && t.status === "open" && !BESIDE.includes(t.type));
      if (holder) {
        if (holder.member_id !== me.id && !manager) {
          throw new ApiError(
            "A colleague already has a follow-up for this customer; ask your manager to change it.",
            "42501",
          );
        }
        this.changeTask(holder, {
          type,
          due_at: dueAt,
          due_has_time: hasTime,
          member_id: assignee,
          reason,
          source,
          created_by_member_id: me.id,
          source_analysis_id: null,
          brief: null,
          place,
        });
        return;
      }
    }
    const now = new Date(nowMs).toISOString();
    const task: DemoTask = {
      id: input.id ?? newId(),
      org_id: this.db.org.id,
      lead_id: lead.id,
      member_id: assignee,
      type,
      due_at: dueAt,
      due_has_time: hasTime,
      reason,
      brief: null,
      source,
      source_rule: null,
      source_analysis_id: null,
      status: "open",
      snoozed_count: 0,
      completed_at: null,
      completed_by_call_id: null,
      created_by_member_id: me.id,
      created_at: now,
      updated_at: now,
      // A visit or meeting is booked by whoever plans it, beside the call follow-up.
      booked_by: BESIDE.includes(type) ? me.id : null,
      place: visitPlace,
      result: null,
    };
    this.db.tasks.push(task);
  }

  async completeFollowUp(id: string, result?: VisitResult | null): Promise<void> {
    const t = this.taskForActor(id);
    if (t.status !== "open") return;
    if (result != null && !["happened", "no_show", "rescheduled"].includes(result)) {
      throw new ApiError("A result is happened, no_show or rescheduled.", "22023");
    }
    if (result != null && !isVisit(t.type)) throw new ApiError("Only a visit or meeting has a result.", "22023");
    this.closeTask(t, "done", result ?? null);
    // A no-show is booked again: a call for whoever booked the visit (DAY-06), as private.upsert_rule_task().
    if (result === "no_show") {
      const due = new Date(Date.now() + 60 * 60_000).toISOString();
      const rule = `rebook_no_show:${t.id}`;
      const open = this.openFollowUps(t.lead_id).filter((x) => !BESIDE.includes(x.type));
      const auto = open.find((x) => !["caller", "owner", "manual"].includes(x.source));
      if (auto) {
        this.changeTask(auto, {
          due_at: due,
          reason: "Missed the visit; book it again",
          type: "call",
          source: "rule",
          source_rule: rule,
        });
      } else if (open[0]) {
        open[0].source_rule = rule;
      } else {
        const now = new Date().toISOString();
        this.db.tasks.push({
          id: newId(),
          org_id: this.db.org.id,
          lead_id: t.lead_id,
          member_id: t.booked_by ?? t.member_id,
          type: "call",
          due_at: due,
          reason: "Missed the visit; book it again",
          brief: null,
          source: "rule",
          source_rule: rule,
          source_analysis_id: null,
          status: "open",
          snoozed_count: 0,
          completed_at: null,
          completed_by_call_id: null,
          created_by_member_id: null,
          created_at: now,
          updated_at: now,
        });
      }
    }
  }

  async snoozeFollowUp(id: string, untilIso: string): Promise<void> {
    const t = this.taskForActor(id);
    if (t.status !== "open") throw new ApiError("This follow-up is already closed.", "22023");
    const until = Date.parse(untilIso);
    const now = Date.now();
    if (!Number.isFinite(until) || until < now - 60_000 || until > now + 30 * 86_400_000) {
      throw new ApiError("Snooze to a time in the next 30 days.", "22023");
    }
    this.changeTask(t, { due_at: new Date(until).toISOString(), snoozed_count: t.snoozed_count + 1 }, true);
  }

  async cancelFollowUp(id: string): Promise<void> {
    const me = this.me();
    const t = this.db.tasks.find((x) =>
      x.id === id && (x.member_id === me.id || x.created_by_member_id === me.id || this.isManager())
    );
    if (!t) throw new ApiError("No such follow-up.", "P0002");
    if (t.status !== "open") return;
    this.closeTask(t, "cancelled");
  }

  async setStage(leadId: string, stageId: string, reason?: string | null): Promise<void> {
    const lead = this.visibleLead(leadId);
    if (!lead) throw new ApiError("No such customer.", "P0002");
    const stage = DEMO_STAGES.find((s) => s.id === stageId);
    if (!stage) throw new ApiError("No such stage.", "P0002");
    const from = lead.stage;
    const lostReason = stage.kind === "lost" ? reason?.trim().slice(0, 200) || null : null;
    const now = new Date().toISOString();
    lead.stage = stage.key as Stage;
    lead.updated_at = now;
    this.crm.lostReasons.set(lead.id, lostReason);
    if (statusOf(from) !== statusOf(lead.stage)) this.crm.closedAt.set(lead.id, stage.kind === "open" ? null : now);
    if (from !== lead.stage) {
      this.logEvent(lead.id, "lead.stage", { from, to: lead.stage, stage_id: stage.id, lost_reason: lostReason }, now);
    }
    // Closing a customer as lost or not a customer ends its open follow-ups.
    if (stage.kind === "lost") {
      for (const t of this.openFollowUps(lead.id)) this.closeTask(t, "cancelled", null, now);
    }
  }

  async setLabel(leadId: string, label: Label | null): Promise<void> {
    if (label !== null && !["hot", "warm", "cold"].includes(label)) {
      throw new ApiError("A label is hot, warm or cold.", "22023");
    }
    const lead = this.visibleLead(leadId);
    if (!lead) throw new ApiError("No such customer.", "P0002");
    const from = this.crm.labels.get(lead.id)?.label ?? null;
    if (from === label) return;
    if (label) this.crm.labels.set(lead.id, { label, at: new Date().toISOString() });
    else this.crm.labels.delete(lead.id);
    this.logEvent(lead.id, "lead.label", { from, to: label });
  }

  /** private.check_may_edit_lead(): a customer she sees, and owns unless she is an owner, admin or manager. */
  private editableLead(leadId: string): Lead {
    const lead = this.visibleLead(leadId);
    if (!lead) throw new ApiError("No such customer.", "P0002");
    if (!this.isManager() && lead.owner_member_id !== this.me().id) {
      throw new ApiError("Only the customer's owner or a manager can change them.", "42501");
    }
    return lead;
  }

  async saveCustomerDetails(leadId: string, name: string | null, details: Record<string, string>): Promise<void> {
    const lead = this.editableLead(leadId);
    const entries = Object.entries(details ?? {});
    for (const [key] of entries) {
      if (!DETAIL_FIELDS.some((f) => f.key === key)) throw new ApiError(`Unknown detail: ${key}.`, "22023");
    }
    const facts = {
      ...(lead.facts && typeof lead.facts === "object" && !Array.isArray(lead.facts) ? lead.facts : {}),
    } as Record<string, Json>;
    for (const [key, value] of entries) {
      const text = (value ?? "").trim();
      if (text) facts[key] = text.slice(0, 120);
      else delete facts[key];
    }
    lead.facts = facts;
    lead.updated_at = new Date().toISOString();
    if (name !== null && name !== undefined) {
      const next = name.trim().slice(0, 120) || null;
      if (next !== lead.name) {
        lead.name = next;
        this.logEvent(lead.id, "lead.details", { name: next, phone: lead.phone_e164 });
      }
    }
  }

  async addCustomerNumber(leadId: string, phone: string, person?: string, role?: string): Promise<void> {
    const lead = this.editableLead(leadId);
    const number = e164(phone);
    if (!number) throw new ApiError("Enter a valid mobile number, e.g. 98765 43210.", "22023");
    if (this.db.excluded.some((x) => x.phone_e164 === number)) {
      throw new ApiError("This number is on your company's excluded list.", "22023");
    }
    const other = this.leadForNumber(number);
    if (other?.id === lead.id) throw new ApiError("This number is already on this customer.", "22023");
    if (other) throw new ApiError("This number belongs to another customer.", "22023");
    const now = new Date().toISOString();
    let contactId: string | null = null;
    if (person?.trim()) {
      contactId = newId();
      this.crm.contacts.push({
        id: contactId,
        lead_id: lead.id,
        name: person.trim().slice(0, 120),
        role: role?.trim().slice(0, 60) || null,
        is_primary: false,
        created_at: now,
      });
    }
    this.crm.numbers.push({
      id: newId(),
      lead_id: lead.id,
      e164: number,
      label: null,
      is_primary: false,
      is_whatsapp: false,
      contact_id: contactId,
      created_at: now,
    });
  }

  async removeCustomerNumber(numberId: string): Promise<void> {
    const added = this.crm.numbers.find((n) => n.id === numberId);
    const main = added ? undefined : this.db.leads.find((l) => `number-${l.id}` === numberId);
    if (!added && !main) throw new ApiError("No such number.", "P0002");
    this.editableLead(added?.lead_id ?? main!.id);
    if (!added) throw new ApiError("The main number cannot be removed.", "22023");
    this.crm.numbers = this.crm.numbers.filter((n) => n.id !== numberId);
    // Their person goes too once no number is left.
    if (added.contact_id && !this.crm.numbers.some((n) => n.contact_id === added.contact_id)) {
      this.crm.contacts = this.crm.contacts.filter((c) => c.id !== added.contact_id);
    }
  }

  async addNote(leadId: string, text: string): Promise<void> {
    const lead = this.visibleLead(leadId);
    if (!lead) throw new ApiError("No such customer.", "P0002");
    const note = (text ?? "").trim();
    if (!note || note.length > 2000) throw new ApiError("A note is 1 to 2000 characters.", "22023");
    // A note is an interaction of its own: it counts as neither a call nor a contact.
    this.logEvent(lead.id, "note.logged", { interaction_id: newId(), text: note });
  }

  /** A customer's new owner (leads_move_follow_ups): the old owner's open follow-ups on them go along. */
  private setOwner(lead: Lead, ownerId: string | null) {
    const from = lead.owner_member_id;
    if (from === ownerId) return;
    const now = new Date().toISOString();
    lead.owner_member_id = ownerId;
    lead.updated_at = now;
    this.crm.assignedAt.set(lead.id, ownerId ? now : null);
    this.logEvent(lead.id, "lead.owner", { from, to: ownerId });
    // Handed to someone, the old owner's open follow-ups go along; left with nobody, to whoever did it (D-164).
    const to = ownerId ?? this.me().id;
    if (from && to !== from) {
      for (const t of this.openFollowUps(lead.id).filter((x) => x.member_id === from)) {
        this.changeTask(t, { member_id: to });
      }
    }
  }

  async assignCustomers(leadIds: string[], ownerId: string | null): Promise<number> {
    if (!this.isManager()) throw new ApiError("Only owners, admins and managers hand customers over.", "42501");
    if (!leadIds.length || leadIds.length > 500) throw new ApiError("Choose 1 to 500 customers.", "22023");
    if (ownerId !== null && !this.activeMember(ownerId)) throw new ApiError("Choose someone in your team.", "22023");
    let changed = 0;
    for (const id of new Set(leadIds)) {
      const lead = this.db.leads.find((l) => l.id === id);
      if (!lead || lead.owner_member_id === ownerId) continue;
      this.setOwner(lead, ownerId);
      changed++;
    }
    return changed;
  }

  async messageTemplates(): Promise<MessageTemplate[]> {
    this.me();
    return this.templates.filter((t) => t.active).map(({ active: _, ...t }) => structuredClone(t));
  }

  async saveMessageTemplate(input: MessageTemplateInput): Promise<string> {
    this.requireAdmin();
    const problem = messageProblem(input);
    if (problem) throw new ApiError(problem, "22023");
    const name = input.name.trim();
    const body = Object.fromEntries(
      Object.entries(input.body).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v),
    );
    if (this.templates.some((t) => t.active && t.name === name && t.id !== input.id)) {
      throw new ApiError("Another message is already called that.", "23505");
    }
    const fields = { name, body, link: input.link?.trim() || null, for_visits: input.for_visits };
    if (input.id) {
      const t = this.templates.find((x) => x.id === input.id && x.active);
      if (!t) throw new ApiError("No such message.", "P0002");
      Object.assign(t, fields);
      return t.id;
    }
    const id = newId();
    this.templates.push({ id, ...fields, active: true });
    return id;
  }

  async removeMessageTemplate(id: string): Promise<void> {
    this.requireAdmin();
    const t = this.templates.find((x) => x.id === id);
    if (t) t.active = false;
  }

  async dispositions(): Promise<Dispositions> {
    this.me();
    return structuredClone(this.dispositionLists);
  }

  async saveDispositions(call: DispositionCall, list: Disposition[]): Promise<Disposition[]> {
    this.requireAdmin();
    const problem = dispositionsProblem(call, list);
    if (problem) throw new ApiError(problem, "22023");
    this.dispositionLists[call] = list.map((d) => ({
      id: d.id ?? newId(),
      label: d.label.trim(),
      label_hi: d.label_hi.trim(),
      acts_as: d.acts_as,
      ...(d.asks_note ? { asks_note: true } : {}),
    }));
    return structuredClone(this.dispositionLists[call]);
  }

  async orderMessageTemplates(ids: string[]): Promise<void> {
    this.requireAdmin();
    const at = (id: string) => (ids.includes(id) ? ids.indexOf(id) : ids.length + this.templates.length);
    this.templates = this.templates.map((t, i) => ({ t, i })).sort((a, b) => at(a.t.id) - at(b.t.id) || a.i - b.i)
      .map(({ t }) => t);
  }

  async logMessage(leadId: string, templateId: string | null): Promise<void> {
    const lead = this.visibleLead(leadId);
    if (!lead) throw new ApiError("No such customer.", "P0002");
    const template = templateId ? this.templates.find((t) => t.id === templateId) : undefined;
    this.logEvent(lead.id, "whatsapp.logged", { interaction_id: newId(), template: template?.name ?? null });
  }

  async callDetail(recordingId: string): Promise<CallDetail> {
    const recording = this.mine(this.db.recordings).find((r) => r.id === recordingId);
    if (!recording) {
      throw new ApiError(
        "Not found, or you do not have access to it.",
        "not_found",
      );
    }
    const analysis = this.db.analyses.find((a) => a.recording_id === recordingId) ?? null;
    return {
      recording,
      call: this.db.calls.find((c) => c.id === recording.call_id) ?? null,
      lead: recording.lead_id ? this.db.leads.find((l) => l.id === recording.lead_id) ?? null : null,
      transcript: this.db.transcripts.find((t) => t.recording_id === recordingId) ?? null,
      analysis,
      insights: this.db.insights.filter((i) => i.recording_id === recordingId),
      reviews: this.db.dispositions.filter((d) => d.recording_id === recordingId)
        .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    };
  }

  async playback(recordingId: string, t?: number | null) {
    const rec = this.mine(this.db.recordings).find((r) => r.id === recordingId);
    if (!rec || rec.erase_requested_at) throw new ApiError("No such recording.", "P0002");
    return { url: demoAudioUrl(rec.duration_s ?? 30), t: t ?? null };
  }

  async rawCalls(
    query: { memberId?: string | null; before?: string | null; limit?: number },
  ): Promise<RawCallRow[]> {
    const limit = query.limit ?? RAW_CALLS_PAGE;
    const names = new Map(this.db.members.map((m) => [m.id, m.display_name]));
    const leadOf = (id: string | null) => (id ? this.db.leads.find((l) => l.id === id) ?? null : null);
    const keep = (memberId: string, at: string) =>
      (!query.memberId || memberId === query.memberId) && (!query.before || at < query.before);
    const recordingOf = (r: Recording) => ({
      id: r.id,
      status: r.status,
      match_method: r.match_method,
      file_name: r.file_name,
      playable: !r.audio_deleted_at && !r.erase_requested_at,
    });
    const rows: RawCallRow[] = [
      ...this.mine(this.db.calls).filter((c) => keep(c.member_id, c.started_at)).map((c): RawCallRow => {
        const rec = this.db.recordings.find((r) => r.call_id === c.id);
        const lead = leadOf(c.lead_id);
        return {
          kind: "call",
          id: c.id,
          at: c.started_at,
          direction: c.direction,
          duration_s: c.duration_s,
          call_status: c.call_status ?? null,
          member_id: c.member_id,
          member_name: names.get(c.member_id) ?? "",
          lead_id: c.lead_id,
          lead_name: lead?.name ?? null,
          phone: c.phone_e164 ?? lead?.phone_e164 ?? c.raw_number,
          contact_name: c.contact_name,
          recording: rec ? recordingOf(rec) : null,
          recording_due: rec ? null : recordingDue(
            c,
            this.db.devices.find((d) => d.id === c.device_id)?.last_sync_at ?? null,
          ),
        };
      }),
      ...this.mine(this.db.recordings).filter((r) => !r.call_id && keep(r.member_id, r.recorded_at ?? r.created_at))
        .map((r): RawCallRow => {
          const lead = leadOf(r.lead_id);
          return {
            kind: "recording",
            id: r.id,
            at: r.recorded_at ?? r.created_at,
            direction: null,
            call_status: null,
            duration_s: r.duration_s,
            member_id: r.member_id,
            member_name: names.get(r.member_id) ?? "",
            lead_id: r.lead_id,
            lead_name: lead?.name ?? null,
            phone: lead?.phone_e164 ?? null,
            contact_name: null,
            recording: recordingOf(r),
          };
        }),
    ];
    return rows.sort((a, b) => b.at.localeCompare(a.at)).slice(0, limit);
  }

  async recordingCounts(): Promise<RecordingCounts> {
    const recs = this.mine(this.db.recordings);
    const n = (statuses: string[]) => recs.filter((r) => statuses.includes(r.status)).length;
    return {
      received: recs.length,
      processed: n(["done"]),
      waiting: n(["queued", "held", "processing"]),
      failed: n(["failed"]),
      without_customer: recs.filter((r) => r.status === "done" && !r.lead_id).length,
    };
  }

  async rerunRecording(recordingId: string, note?: { layer: NoteLayer; text: string }): Promise<void> {
    // Owners and admins, and senior reviewers (D-116).
    if (this.me().qa_role !== "senior") this.requireAdmin();
    const text = note?.text.trim() ?? "";
    if (text && !["transcript", "analysis", "both"].includes(note?.layer ?? "")) {
      throw new ApiError("Say where the mistake was: the transcript, the analysis or both.", "22023");
    }
    const rec = this.db.recordings.find((r) => r.id === recordingId);
    if (!rec) throw new ApiError("No such recording.", "P0002");
    if (rec.audio_deleted_at || rec.erase_requested_at) {
      throw new ApiError("The recording's audio is gone, so it cannot be run again.", "22023");
    }
    if (rec.status === "processing") {
      throw new ApiError("The call is being processed now; try again in a minute.", "55006");
    }
    // The same steps as rerun_recording(): the note is kept with what the AI had said; the analysis and its findings
    // go, and the transcript too unless the note is about the analysis alone (D-111); the call waits again.
    const keepTranscript = text !== "" && note?.layer === "analysis";
    if (text) {
      const analysis = this.db.analyses.find((a) => a.recording_id === recordingId);
      const call = this.db.calls.find((c) => c.id === rec.call_id);
      const task = analysis && this.db.tasks.find((t) => t.source_analysis_id === analysis.id);
      const before: NoteReading = {
        ...(analysis ? { summary: analysis.summary ?? undefined, prompt_version: analysis.prompt_version } : {}),
        ...(task ? { follow_up: { due_at: task.due_at, type: task.type, source: task.source } } : {}),
        ...(!keepTranscript
          ? { transcript: this.db.transcripts.find((t) => t.recording_id === recordingId)?.text ?? undefined }
          : {}),
      };
      const lead = this.db.leads.find((l) => l.id === (call?.lead_id ?? rec.lead_id));
      this.db.callNotes.unshift({
        id: newId(),
        created_at: new Date().toISOString(),
        layer: note!.layer,
        note: text,
        call_id: rec.call_id,
        recording_id: rec.id,
        author: this.me().display_name,
        caller: this.db.members.find((m) => m.id === rec.member_id)?.display_name ?? null,
        call: call
          ? {
            started_at: call.started_at,
            direction: call.direction,
            duration_s: call.duration_s,
            lead_id: call.lead_id,
            customer: lead?.name || lead?.phone_e164 || null,
          }
          : null,
        recording_status: "queued",
        before,
        now: null,
      });
    }
    const analyses = new Set(this.db.analyses.filter((a) => a.recording_id === recordingId).map((a) => a.id));
    this.db.analyses = this.db.analyses.filter((a) => !analyses.has(a.id));
    this.db.insights = this.db.insights.filter((i) => !analyses.has(i.analysis_id));
    if (!keepTranscript) this.db.transcripts = this.db.transcripts.filter((t) => t.recording_id !== recordingId);
    Object.assign(rec, { status: "queued", attempts: 0, error_kind: null, error: null, processed_at: null });
  }

  async callNotes(): Promise<CallNote[]> {
    this.requireAdmin();
    return this.db.callNotes.map((n) => ({ ...n }));
  }

  // ------------------------------------------------------------ call review (D-112), as the qa_* functions do

  private requireReviewer() {
    const me = this.me();
    if (!me.qa_role && me.role !== "owner" && me.role !== "admin") {
      throw new ApiError("Only reviewers, owners and admins review calls.", "42501");
    }
  }

  async noteDashboardVisit() {
    // The demo keeps no visits; the made-up companies say when their managers were last on the dashboard.
  }

  async qaMe(): Promise<QaMe> {
    const me = this.me();
    const canManage = me.role === "owner" || me.role === "admin";
    return {
      qa_role: (me.qa_role as QaRole | null) ?? null,
      can_manage: canManage,
      can_review: !!me.qa_role || canManage,
    };
  }

  /** Processed calls with a transcript and a reading; those whose reading set a follow-up first (D-138), oldest first. */
  private qaReviewable(): string[] {
    return this.db.recordings
      .filter((r) =>
        r.status === "done" && !r.erase_requested_at &&
        this.db.transcripts.some((t) => t.recording_id === r.id) &&
        this.db.analyses.some((a) => a.recording_id === r.id)
      )
      .map((r) => ({
        id: r.id,
        at: this.db.calls.find((c) => c.id === r.call_id)?.started_at ?? r.recorded_at ?? r.created_at,
        followUp: this.db.analyses.some((a) =>
          a.recording_id === r.id && this.db.tasks.some((t) => t.source === "ai" && t.source_analysis_id === a.id)
        ),
      }))
      .sort((a, b) => Number(b.followUp) - Number(a.followUp) || a.at.localeCompare(b.at) || a.id.localeCompare(b.id))
      .map((x) => x.id);
  }

  async qaNext(stage: QaStage): Promise<QaNext> {
    this.requireReviewer();
    const me = this.me().id;
    const pool = stage === "tester" ? this.qaReviewable() : [
      ...new Set(
        this.db.qaReviews
          .filter((q) => q.stage === "tester" && q.flagged)
          .sort((a, b) => a.updated_at.localeCompare(b.updated_at))
          .map((q) => q.recording_id),
      ),
    ];
    const mine = new Set(
      this.db.qaReviews.filter((q) => q.reviewer_member_id === me && q.stage === stage).map((q) => q.recording_id),
    );
    const done = pool.filter((id) => mine.has(id)).length;
    return {
      recording_id: pool.find((id) => !mine.has(id)) ?? null,
      total: pool.length,
      done,
      left: pool.length - done,
    };
  }

  async qaCall(recordingId: string): Promise<QaCall> {
    this.requireReviewer();
    const me = this.me();
    const rec = this.db.recordings.find((r) => r.id === recordingId);
    if (!rec || rec.erase_requested_at) throw new ApiError("No such call.", "P0002");
    const call = this.db.calls.find((c) => c.id === rec.call_id);
    const lead = this.db.leads.find((l) => l.id === (call?.lead_id ?? rec.lead_id));
    const analysis = this.db.analyses
      .filter((a) => a.recording_id === rec.id)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
    const transcript = this.db.transcripts.find((t) => t.recording_id === rec.id);
    const task = analysis && this.db.tasks.find((t) => t.source_analysis_id === analysis.id);
    const number = call?.phone_e164 ?? lead?.phone_e164 ?? rec.phone_hint ?? null;
    const seesAll = me.role === "owner" || me.role === "admin" || me.qa_role === "senior";
    const name = (id: string) => this.db.members.find((m) => m.id === id)?.display_name ?? "";
    return {
      call: {
        recording_id: rec.id,
        call_id: rec.call_id,
        started_at: call?.started_at ?? rec.recorded_at ?? rec.created_at,
        direction: call?.direction ?? null,
        duration_s: rec.duration_s ?? call?.duration_s ?? null,
        caller: name(rec.member_id),
        customer: lead?.name || null,
        number_end: number ? number.slice(-4) : null,
        lead_stage: lead?.stage ?? null,
        lead_interest: lead?.interest ?? null,
        audio_deleted: !!rec.audio_deleted_at,
      },
      transcript: transcript
        ? { model: transcript.model, text: transcript.text, segments: transcript.segments as unknown as Segment[] }
        : null,
      analysis: analysis
        ? {
          id: analysis.id,
          prompt_version: analysis.prompt_version,
          model: analysis.model,
          created_at: analysis.created_at,
          output: analysis.output as unknown as AnalysisOutput,
          summary: analysis.summary,
          interest: analysis.interest,
        }
        : null,
      insights: this.db.insights
        .filter((i) => analysis && i.analysis_id === analysis.id)
        .map((i) => ({ kind: i.kind, category: i.category, detail: i.detail, quote: i.quote, t: i.t })),
      outcome: {
        ai_outcome: null,
        her_choice: null,
        her_choice_at: null,
        follow_up: task
          ? {
            type: task.type,
            due_at: task.due_at,
            due_has_time: true,
            reason: task.reason,
            source: task.source,
            status: task.status,
          }
          : null,
      },
      checks: this.db.qaChecks.map((c) => ({ ...c })),
      reviews: this.db.qaReviews
        .filter((q) => q.recording_id === rec.id && (q.reviewer_member_id === me.id || seesAll))
        .sort((a, b) => b.stage.localeCompare(a.stage) || a.updated_at.localeCompare(b.updated_at))
        .map((q) => ({
          stage: q.stage,
          reviewer: name(q.reviewer_member_id),
          mine: q.reviewer_member_id === me.id,
          answers: { ...q.answers },
          note: q.note,
          flagged: q.flagged,
          layer: q.layer,
          prompt_version: q.prompt_version,
          updated_at: q.updated_at,
        })),
    };
  }

  async qaSave(
    recordingId: string,
    stage: QaStage,
    review: { answers: Record<string, boolean>; note?: string; flag?: boolean; layer?: QaLayer | null },
  ): Promise<{ flagged: boolean; next: QaNext }> {
    this.requireReviewer();
    if (review.layer && stage !== "senior") {
      throw new ApiError(
        "Only a senior says where the error was: the transcript, the analysis, both or neither.",
        "22023",
      );
    }
    const rec = this.db.recordings.find((r) => r.id === recordingId);
    if (!rec) throw new ApiError("No such call.", "P0002");
    const keys = new Set(this.db.qaChecks.map((c) => c.key));
    const answers = Object.fromEntries(
      Object.entries(review.answers).filter(([k, v]) => keys.has(k) && typeof v === "boolean"),
    );
    const flagged = !!review.flag || Object.values(answers).some((v) => v === false);
    const me = this.me().id;
    const analysis = this.db.analyses.find((a) => a.recording_id === recordingId);
    const row = {
      recording_id: recordingId,
      reviewer_member_id: me,
      stage,
      answers,
      note: review.note?.trim() || null,
      flagged,
      layer: review.layer ?? null,
      prompt_version: analysis?.prompt_version ?? null,
      updated_at: new Date().toISOString(),
    };
    const i = this.db.qaReviews.findIndex((q) =>
      q.recording_id === recordingId && q.reviewer_member_id === me && q.stage === stage
    );
    if (i >= 0) this.db.qaReviews[i] = row;
    else this.db.qaReviews.push(row);
    return { flagged, next: await this.qaNext(stage) };
  }

  async qaAddCheck(label: string): Promise<QaCheck> {
    this.requireReviewer();
    const text = label.trim();
    if (text.length < 2 || text.length > 160) throw new ApiError("Write the check as a short question.", "22023");
    if (this.db.qaChecks.some((c) => c.label.toLowerCase() === text.toLowerCase())) {
      throw new ApiError("That check is already there.", "23505");
    }
    let key = text.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 32);
    if (!/^[a-z]/.test(key)) key = `check_${key}`;
    let unique = key;
    for (let n = 2; this.db.qaChecks.some((c) => c.key === unique); n++) unique = `${key}_${n}`;
    const check = { key: unique, label: text, built_in: false };
    this.db.qaChecks.push(check);
    return { ...check };
  }

  async qaSetRole(memberId: string, role: QaRole | null): Promise<void> {
    this.requireAdmin();
    if (role !== null) this.requireAi();
    const m = this.db.members.find((x) => x.id === memberId);
    if (!m) throw new ApiError("No such member.", "P0002");
    m.qa_role = role;
  }

  async qaSummary(): Promise<QaSummary> {
    this.requireReviewer();
    const tester = this.db.qaReviews.filter((q) => q.stage === "tester");
    const layers: Partial<Record<QaLayer, number>> = {};
    for (const q of this.db.qaReviews) {
      if (q.stage === "senior" && q.layer) layers[q.layer] = (layers[q.layer] ?? 0) + 1;
    }
    return {
      reviewable: this.qaReviewable().length,
      reviewers: this.db.members
        .filter((m) => m.status === "active" && m.qa_role)
        .map((m) => ({
          member_id: m.id,
          name: m.display_name,
          qa_role: m.qa_role as QaRole,
          reviewed: tester.filter((q) => q.reviewer_member_id === m.id).length,
          flagged: tester.filter((q) => q.reviewer_member_id === m.id && q.flagged).length,
          verdicts: this.db.qaReviews.filter((q) => q.reviewer_member_id === m.id && q.stage === "senior").length,
          last_at: this.db.qaReviews.filter((q) => q.reviewer_member_id === m.id).map((q) => q.updated_at).sort()
            .pop() ?? null,
        })),
      checks: this.db.qaChecks.map((c) => ({
        key: c.key,
        label: c.label,
        yes: tester.filter((q) => q.answers[c.key] === true).length,
        no: tester.filter((q) => q.answers[c.key] === false).length,
      })),
      flagged_calls: new Set(tester.filter((q) => q.flagged).map((q) => q.recording_id)).size,
      layers,
    };
  }

  async review(
    analysisId: string,
    action: ReviewAction,
    patch?: { type?: TaskType; dueAt?: string },
  ) {
    const analysis = this.db.analyses.find((a) => a.id === analysisId);
    const rec = analysis &&
      this.mine(this.db.recordings).find((r) => r.id === analysis.recording_id);
    if (!analysis || !rec) throw new ApiError("No such call.", "P0002");
    // A telecaller does not overrule a manager's review of the same call (REVIEW-01), as the database refuses it.
    const managerReviewed = this.db.dispositions.some((d) =>
      d.analysis_id === analysisId &&
      this.db.members.some((m) => m.id === d.member_id && m.role !== "caller")
    );
    if (!this.isManager() && managerReviewed) {
      throw new ApiError("Your manager has already reviewed this call.", "42501");
    }
    const task = this.db.tasks.find((t) => t.lead_id === analysis.lead_id && t.status === "open") ?? null;
    const corrected: Record<string, Json> = {};
    if (action === "corrected") {
      if (!task || (!patch?.type && !patch?.dueAt)) {
        throw new ApiError("A correction must change something.", "22023");
      }
      if (patch.type && patch.type !== task.type) {
        corrected.type = { from: task.type, to: patch.type };
      }
      if (patch.dueAt) {
        corrected.due_at = { from: task.due_at, to: patch.dueAt };
      }
      if (patch.type) task.type = patch.type;
      if (patch.dueAt) task.due_at = patch.dueAt;
    }
    if (action === "not_a_lead") {
      const lead = this.db.leads.find((l) => l.id === analysis.lead_id);
      if (lead) lead.stage = "not_a_lead";
      if (task) task.status = "cancelled";
    }
    this.db.dispositions.push({
      id: newId(),
      org_id: this.db.org.id,
      member_id: this.me().id,
      recording_id: rec.id,
      analysis_id: analysisId,
      lead_id: analysis.lead_id,
      task_id: task?.id ?? null,
      action,
      corrected,
      task_type: action === "not_a_lead" ? null : task?.type ?? null,
      task_due_at: action === "not_a_lead" ? null : task?.due_at ?? null,
      created_at: new Date().toISOString(),
    });
  }

  async openTasks(memberId: string | null): Promise<TaskWithLead[]> {
    return this.mine(this.db.tasks).filter((t) =>
      (memberId === null || t.member_id === memberId) && t.status === "open"
    )
      .sort((a, b) => a.due_at.localeCompare(b.due_at))
      .map((t) => {
        const l = this.db.leads.find((x) => x.id === t.lead_id);
        return {
          ...t,
          lead: l
            ? {
              id: l.id,
              name: l.name,
              phone_e164: l.phone_e164,
              stage: l.stage,
              interest: l.interest,
            }
            : null,
        };
      });
  }

  async completeTask(id: string) {
    const t = this.visibleTask(id);
    t.status = "done";
    t.completed_at = new Date().toISOString();
  }

  async snoozeTask(id: string, minutes: number) {
    if (minutes < 5 || minutes > 1440) {
      throw new ApiError("Snooze for 5 minutes to a day.", "22023");
    }
    const t = this.visibleTask(id);
    const base = Math.max(Date.now(), new Date(t.due_at).getTime());
    t.due_at = new Date(base + minutes * 60_000).toISOString();
    t.snoozed_count += 1;
  }

  async rescheduleTask(id: string, dueAt: string, type?: TaskType) {
    const t = this.visibleTask(id);
    if (new Date(dueAt).getTime() > Date.now() + 366 * 86_400_000) {
      throw new ApiError("Not more than a year ahead.", "22023");
    }
    t.due_at = dueAt;
    if (type) t.type = type;
  }

  async addTask(
    leadId: string,
    type: TaskType,
    dueAt: string,
    reason?: string,
  ) {
    const lead = this.visibleLead(leadId);
    if (!lead) throw new ApiError("No such lead.", "P0002");
    // The customer's one open follow-up; visits and meetings sit beside it (TASK-01, D-163).
    const open = this.db.tasks.find((t) => t.lead_id === leadId && t.status === "open" && !BESIDE.includes(t.type));
    if (open) {
      Object.assign(open, {
        type,
        due_at: dueAt,
        reason: reason || open.reason,
      });
      return;
    }
    const now = new Date().toISOString();
    this.db.tasks.push({
      id: newId(),
      org_id: this.db.org.id,
      lead_id: leadId,
      member_id: lead.owner_member_id ?? this.me().id,
      type,
      due_at: dueAt,
      reason: reason || "Follow-up",
      brief: null,
      source: "manual",
      source_rule: null,
      source_analysis_id: null,
      status: "open",
      snoozed_count: 0,
      completed_at: null,
      completed_by_call_id: null,
      created_by_member_id: this.me().id,
      created_at: now,
      updated_at: now,
    });
  }

  // ------------------------------------------------------------------ team

  async team() {
    const me = this.me();
    const admin = ["owner", "admin"].includes(me.role);
    return {
      members: [...this.db.members].sort((a, b) => a.display_name.localeCompare(b.display_name)),
      invitations: admin ? this.db.invitations.filter((i) => !i.accepted_at && !i.revoked_at) : [],
      // Managers see the team's phones too (DEV-02); revoking one stays with owners and admins.
      devices: this.isManager() ? this.db.devices : this.db.devices.filter((d) => d.member_id === me.id),
    };
  }

  async teamHealth(): Promise<TeamHealth> {
    this.requireManager();
    const now = new Date();
    const dayStart = new Date(now);
    dayStart.setHours(0, 0, 0, 0);
    const minutesAgo = (m: number) => new Date(now.getTime() - m * 60_000).toISOString();
    const members = this.db.members.filter((m) => m.status === "active");
    return {
      now: now.toISOString(),
      day_start: dayStart.toISOString(),
      counting: { after_call_since: dayStart.toISOString(), ringing_since: dayStart.toISOString() },
      members: members
        .map((m, i) => {
          const d = this.db.devices.find((x) => x.member_id === m.id && !x.revoked_at);
          // Made-up activity: one phone signing in again and again, one quiet since the morning, one on a call.
          const today = d ? 18 + i * 7 : 0;
          return {
            member_id: m.id,
            name: m.display_name,
            role: m.role,
            phone: m.phone_e164,
            notice_accepted: true,
            device: d
              ? {
                manufacturer: d.manufacturer,
                model: d.model,
                os_version: d.os_version,
                app_version: d.app_version,
                installed_at: d.created_at,
                last_seen_at: i === 3 ? minutesAgo(190) : minutesAgo(1 + i),
                last_sync_at: i === 3 ? minutesAgo(250) : minutesAgo(40 + i),
                sync_problem: d.sync_problem,
                files_waiting: d.files_waiting,
                permissions: d.permissions,
              }
              : null,
            signins_today: d ? (i === 2 ? 5 : 1) : 0,
            last_signin_at: d ? minutesAgo(i === 2 ? 12 : 300) : null,
            live: d && i === 2 ? { state: "calling", since: minutesAgo(2), updated_at: minutesAgo(0) } : null,
            calls: {
              last_15_min: d && i !== 3 ? 2 + (i % 3) : 0,
              last_hour: d && i !== 3 ? 6 + i : 0,
              today,
              outgoing: Math.round(today * 0.7),
              incoming: Math.round(today * 0.2),
              missed: today - Math.round(today * 0.7) - Math.round(today * 0.2),
              connected: Math.round(today * 0.55),
              choices: Math.round(today * 0.45),
              // One phone whose own recorder misses calls, beyond the files still waiting on it (TEAM-14).
              answered_10s: Math.round(today * 0.5),
              unrecorded: d && i === 3 ? 6 : 0,
              last_at: d ? (i === 3 ? minutesAgo(195) : minutesAgo(2 + i)) : null,
              last_synced_at: d ? (i === 3 ? minutesAgo(190) : minutesAgo(1 + i)) : null,
            },
            // One phone whose ringing pop-up never shows, one with hardly a choice after calls.
            after_call: d
              ? {
                shown_today: Math.round(today * 0.8),
                shown_last_hour: d && i !== 3 ? 4 + i : 0,
                choices: i === 1 ? 1 : Math.round(today * 0.45),
              }
              : null,
            ringing: d
              ? { incoming: Math.round(today * 0.3), with_popup: i === 2 ? 0 : Math.round(today * 0.3) - 1 }
              : null,
          };
        })
        .sort((a, b) => Number(b.role === "caller") - Number(a.role === "caller") || a.name.localeCompare(b.name)),
    };
  }

  async invite(
    input: { displayName: string; role: Role; phone?: string; email?: string },
  ) {
    this.requireAdmin();
    if (
      (input.role === "owner" || input.role === "admin") &&
      this.me().role !== "owner"
    ) {
      throw new ApiError("Only an owner can invite owners or admins.", "42501");
    }
    const phone = input.phone ? e164(input.phone) : null;
    if (input.phone && !phone) {
      throw new ApiError("That is not a valid phone number.", "22023");
    }
    if (!phone && !input.email) {
      throw new ApiError("Give a phone number or an email.", "22023");
    }
    this.db.invitations.unshift({
      id: newId(),
      org_id: this.db.org.id,
      display_name: input.displayName,
      role: input.role,
      phone_e164: phone,
      email: input.email?.toLowerCase() || null,
      invited_by: this.me().id,
      expires_at: new Date(Date.now() + 7 * 86_400_000).toISOString(),
      accepted_at: null,
      accepted_member_id: null,
      revoked_at: null,
      created_at: new Date().toISOString(),
    });
  }

  async revokeInvitation(id: string) {
    this.requireAdmin();
    const inv = this.db.invitations.find((i) => i.id === id);
    if (inv) inv.revoked_at = new Date().toISOString();
  }

  async revokeDevice(id: string) {
    this.requireAdmin();
    const d = this.db.devices.find((x) => x.id === id);
    if (!d) throw new ApiError("No such device.", "P0002");
    if (!d.revoked_at) {
      d.revoked_at = new Date().toISOString();
      d.revoked_by = this.me().id;
    }
  }

  async removeMember(id: string) {
    this.requireAdmin();
    const me = this.me();
    const target = this.db.members.find((m) => m.id === id && m.status === "active");
    if (!target) throw new ApiError("No such member.", "P0002");
    if (target.id === me.id) {
      throw new ApiError("You cannot remove yourself.", "22023");
    }
    if (
      (target.role === "owner" || target.role === "admin") &&
      me.role !== "owner"
    ) {
      throw new ApiError("Only an owner can remove owners or admins.", "42501");
    }
    target.status = "removed";
    target.removed_at = new Date().toISOString();
    this.db.tasks.filter((t) => t.member_id === id && t.status === "open")
      .forEach((t) => (t.member_id = me.id));
    this.db.leads.filter((l) => l.owner_member_id === id).forEach((
      l,
    ) => (l.owner_member_id = null));
    this.db.devices.filter((d) => d.member_id === id && !d.revoked_at).forEach(
      (d) => {
        d.revoked_at = new Date().toISOString();
        d.revoked_by = me.id;
      },
    );
  }

  async setRole(memberId: string, role: Role) {
    this.requireAdmin();
    const me = this.me();
    const target = this.db.members.find((m) => m.id === memberId);
    if (!target) throw new ApiError("No such member.", "P0002");
    if (
      me.role !== "owner" &&
      (role === "owner" || role === "admin" || target.role === "owner" ||
        target.role === "admin")
    ) {
      throw new ApiError(
        "Only an owner can make or unmake owners and admins.",
        "42501",
      );
    }
    if (
      target.role === "owner" && role !== "owner" &&
      this.db.members.filter((m) => m.role === "owner" && m.status === "active")
          .length === 1
    ) {
      throw new ApiError(
        "An organization must keep at least one owner.",
        "22023",
      );
    }
    target.role = role;
  }

  // ------------------------------------------------------ import, settings

  /** What import_leads() does (LEAD-06, LEAD-16), in memory: one customer per number, every row an enquiry once. */
  async importLeads(rows: ImportRow[], options?: ImportOptions): Promise<ImportResult> {
    this.requireManager();
    const opts: ImportOptions = options ?? { import_id: newId(), assign: { mode: "column" } };
    const mode = opts.assign?.mode ?? "column";
    const people = mode === "one" || mode === "split"
      ? (opts.assign.members ?? []).map((id) => this.db.members.find((m) => m.id === id && m.status === "active"))
      : [];
    if (people.some((p) => !p) || ((mode === "one" || mode === "split") && people.length === 0)) {
      throw new ApiError("Leads can go only to active members of your team.", "22023");
    }
    const importSource = opts.source_id
      ? this.db.sources.find((x) => x.id === opts.source_id)
      : this.db.sources.find((x) => x.type === "import");
    if (opts.source_id && !importSource) throw new ApiError("Unknown source.", "22023");
    const now = new Date().toISOString();
    let imp = this.db.leadImports.find((i) => i.id === opts.import_id);
    if (!imp) {
      imp = {
        id: opts.import_id,
        org_id: this.db.org.id,
        created_by: this.me().id,
        file_name: opts.file_name ?? null,
        format: opts.format ?? "csv",
        source_id: importSource?.id ?? null,
        assignment: { mode, members: opts.assign.members ?? [] } as Json,
        rows_seen: 0,
        inserted: 0,
        updated: 0,
        enquiries: 0,
        skipped: 0,
        assigned_new: 0,
        created_at: now,
        updated_at: now,
      };
      this.db.leadImports.unshift(imp);
    }
    const result: ImportResult = {
      import_id: imp.id,
      inserted: 0,
      updated: 0,
      enquiries: 0,
      repeats: 0,
      invalid: [],
      notes: [],
    };
    const known = new Set<string>();
    const fresh = new Set<string>();
    rows.forEach((r, i) => {
      const phone = e164(r.phone.replace(/^[a-z]{1,3}:\s*/i, ""));
      const owner = r.owner_phone ? this.db.members.find((m) => m.phone_e164 === e164(r.owner_phone!)) : undefined;
      const reason = !phone
        ? "not a valid phone number"
        : this.db.excluded.some((x) => x.phone_e164 === phone)
        ? "number is on the excluded list"
        : this.db.members.some((m) => m.phone_e164 === phone && m.status === "active")
        ? "number belongs to a team member"
        : mode === "column" && r.owner_phone && !owner
        ? "owner phone is not an active team member"
        : null;
      if (reason || !phone) {
        result.invalid.push({ row: i, phone: r.phone, reason: reason ?? "not a valid phone number" });
        return;
      }
      const source = (r.source && this.db.sources.find((x) => x.name.toLowerCase() === r.source!.toLowerCase())) ||
        importSource;
      if (r.source && source === importSource) {
        result.notes!.push({
          row: i,
          note: `the source "${r.source}" is not one of your sources; the import's source was used`,
        });
      }
      const when = r.enquired_at && !Number.isNaN(Date.parse(r.enquired_at)) ? new Date(r.enquired_at) : null;
      if (r.enquired_at && !when) {
        result.notes!.push({
          row: i,
          note: `the time "${r.enquired_at}" was not understood; the import time was used`,
        });
      }
      let lead = this.db.leads.find((l) => l.phone_e164 === phone);
      if (!lead) {
        const to = mode === "one" || mode === "split"
          ? people[imp!.assigned_new++ % people.length]!.id
          : mode === "column"
          ? owner?.id ?? null
          : null;
        lead = {
          id: newId(),
          org_id: this.db.org.id,
          phone_e164: phone,
          name: r.name ?? null,
          owner_member_id: to,
          stage: "new",
          interest: null,
          summary: null,
          facts: Object.fromEntries(
            (["requirement", "location", "budget"] as const).filter((k) => r[k]).map((k) => [k, r[k]!]),
          ),
          source: "import",
          source_id: source?.id ?? null,
          campaign: r.campaign_name?.slice(0, 120) ?? null,
          first_contact_at: null,
          last_contact_at: null,
          last_enquiry_at: null,
          created_at: now,
          updated_at: now,
        };
        this.db.leads.push(lead);
        fresh.add(phone);
      } else if (!fresh.has(phone)) {
        known.add(phone);
        if (!lead.name && r.name) lead.name = r.name;
        if (!lead.owner_member_id && mode !== "none") {
          lead.owner_member_id = mode === "column"
            ? owner?.id ?? null
            : people[imp!.assigned_new++ % people.length]!.id;
        }
      }
      const at = (when ?? new Date(now)).toISOString();
      if (!lead.last_enquiry_at || at > lead.last_enquiry_at) lead.last_enquiry_at = at;
      const fingerprint = r.external_id ? `id|${r.external_id.replace(/^[a-z]{1,3}:/, "")}` : JSON.stringify([
        phone,
        source?.id,
        r,
      ]);
      if (this.db.enquiries.some((e) => e.fingerprint === fingerprint)) {
        result.repeats!++;
        return;
      }
      const id = (v?: string) => (v ? v.replace(/^\s*[a-z]{1,3}:/, "") : null);
      this.db.enquiries.push({
        id: newId(),
        org_id: this.db.org.id,
        lead_id: lead.id,
        import_id: imp!.id,
        source_id: source?.id ?? null,
        enquired_at: at,
        time_from_file: !!when,
        external_id: id(r.external_id),
        platform: r.platform ?? null,
        is_organic: r.is_organic === undefined ? null : ["true", "yes", "1"].includes(r.is_organic.toLowerCase()),
        campaign_id: id(r.campaign_id),
        campaign_name: r.campaign_name ?? null,
        adset_id: id(r.adset_id),
        adset_name: r.adset_name ?? null,
        ad_id: id(r.ad_id),
        ad_name: r.ad_name ?? null,
        form_id: id(r.form_id),
        form_name: r.form_name ?? null,
        answers: (r.answers ?? []) as Json,
        contact: { name: r.name, phone: r.phone, email: r.email, city: r.city } as Json,
        fingerprint,
        created_by: this.me().id,
        created_at: now,
      });
      result.enquiries!++;
    });
    result.inserted = fresh.size;
    result.updated = known.size;
    imp.rows_seen += rows.length;
    imp.inserted += result.inserted;
    imp.updated += result.updated;
    imp.enquiries += result.enquiries!;
    imp.skipped += result.invalid.length;
    return result;
  }

  async sources() {
    this.me();
    return [...this.db.sources];
  }

  async leadImports() {
    this.requireManager();
    return this.db.leadImports.slice(0, 20);
  }

  async leadEnquiries(leadId: string) {
    this.me();
    return this.db.enquiries.filter((e) => e.lead_id === leadId)
      .sort((a, b) => b.enquired_at.localeCompare(a.enquired_at));
  }

  async settings() {
    this.me();
    return {
      org: { ...this.db.org },
      holidays: [...this.db.holidays],
      excluded: [...this.db.excluded],
    };
  }

  async saveOrg(
    patch: {
      name?: string;
      timezone?: string;
      working_hours?: Json;
      settings?: Json;
    },
  ) {
    this.requireAdmin();
    Object.assign(this.db.org, patch, { updated_at: new Date().toISOString() });
  }

  async addHoliday(day: string, name: string) {
    this.requireAdmin();
    if (this.db.holidays.some((h) => h.day === day)) {
      throw new ApiError("That day is already a holiday.", "23505");
    }
    this.db.holidays.push({
      id: newId(),
      org_id: this.db.org.id,
      day,
      name: name || null,
      created_at: new Date().toISOString(),
    });
    this.db.holidays.sort((a, b) => a.day.localeCompare(b.day));
  }

  async deleteHoliday(id: string) {
    this.requireAdmin();
    this.db.holidays = this.db.holidays.filter((h) => h.id !== id);
  }

  async addExcluded(phone: string, reason: string) {
    this.requireAdmin();
    const p = e164(phone);
    if (!p) throw new ApiError("That is not a valid phone number.", "22023");
    this.db.excluded.unshift({
      id: newId(),
      org_id: this.db.org.id,
      phone_e164: p,
      reason: reason || null,
      added_by: this.me().id,
      created_at: new Date().toISOString(),
    });
  }

  async deleteExcluded(id: string) {
    this.requireAdmin();
    this.db.excluded = this.db.excluded.filter((x) => x.id !== id);
  }

  // ------------------------------------------------------- AI instructions

  async aiInstructions(): Promise<AiInstructions> {
    this.me();
    return structuredClone(this.db.aiInstructions);
  }

  async saveAiInstructions(patch: Partial<Record<TextField, string>> & { checklist?: Json; facts?: Json }) {
    this.requireAdmin();
    this.requireAi();
    const current = this.db.aiInstructions;
    const next: AiInstructions = {
      ...current,
      ...Object.fromEntries(
        (Object.keys(TEXT_LIMITS) as TextField[]).filter((k) => patch[k] !== undefined)
          .map((k) => [k, patch[k]!]),
      ),
      checklist: patch.checklist !== undefined ? patch.checklist as unknown as ChecklistItem[] : current.checklist,
      facts: patch.facts !== undefined ? patch.facts as unknown as FactField[] : current.facts,
    };
    const problem = (Object.keys(TEXT_LIMITS) as TextField[]).map((k) => textProblem(k, next[k])).find(Boolean) ??
      checklistProblem(next.checklist) ?? factsProblem(next.facts);
    if (problem) throw new ApiError(problem, "22023");
    const strip = ({ version: _v, updated_at: _a, updated_by: _b, ...rest }: AiInstructions) => JSON.stringify(rest);
    if (strip(next) === strip(current)) return;
    this.db.aiInstructions = {
      ...next,
      version: current.version + 1,
      updated_at: new Date().toISOString(),
      updated_by: this.me().id,
    };
  }

  async objectionCategories() {
    this.me();
    return [...this.db.taxonomies].sort((a, b) => a.sort - b.sort);
  }

  async saveObjectionCategory(id: string, patch: { label?: string; description?: string | null; active?: boolean }) {
    this.requireAdmin();
    const row = this.db.taxonomies.find((t) => t.id === id);
    if (!row) throw new ApiError("No such category.", "P0002");
    if (patch.active === false && row.category === "other") {
      throw new ApiError("Other is always shown.", "23514");
    }
    if (patch.label !== undefined && (patch.label.trim().length < 2 || patch.label.trim().length > 40)) {
      throw new ApiError("A category name has 2 to 40 characters.", "23514");
    }
    if (patch.active && !row.active && this.db.taxonomies.filter((t) => t.active).length >= MAX_CATEGORIES) {
      throw new ApiError(`At most ${MAX_CATEGORIES} categories are shown. Hide one first.`, "22023");
    }
    Object.assign(row, patch);
  }

  async addObjectionCategory(label: string, description: string) {
    this.requireAdmin();
    const name = label.trim();
    if (name.length < 2 || name.length > 40) throw new ApiError("A category name has 2 to 40 characters.", "22023");
    let category = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);
    if (!/^[a-z][a-z0-9_]{1,39}$/.test(category)) category = `c_${String(Date.now()).slice(-10)}`;
    const existing = this.db.taxonomies.find((t) => t.category === category);
    if (existing?.active) throw new ApiError(`There is already a category called ${existing.label}.`, "23505");
    if (this.db.taxonomies.filter((t) => t.active).length >= MAX_CATEGORIES) {
      throw new ApiError(`At most ${MAX_CATEGORIES} categories are shown. Hide one first.`, "22023");
    }
    const sort = Math.max(0, ...this.db.taxonomies.filter((t) => t.category !== "other").map((t) => t.sort)) + 1;
    if (existing) {
      Object.assign(existing, { label: name, description: description.trim() || null, active: true, sort });
    } else {
      this.db.taxonomies.push({
        id: newId(),
        org_id: this.db.org.id,
        kind: "objection",
        category,
        label: name,
        description: description.trim() || null,
        active: true,
        sort,
        created_at: new Date().toISOString(),
      });
    }
    const other = this.db.taxonomies.find((t) => t.category === "other");
    if (other && other.sort <= sort) other.sort = sort + 1;
  }

  async liveCall(): Promise<LiveCall | null> {
    // The telecaller is on a call with her first customer, so the card can be seen in the demo.
    const me = this.me();
    const lead = me.role === "caller" ? this.db.leads.find((l) => l.owner_member_id === me.id) : undefined;
    if (!lead) return null;
    // One demo call per browser, so Hide stays hidden across reloads, as it does for a real call.
    const started = read("callscribe.demo.liveCallStarted") ?? new Date(Date.now() - 74_000).toISOString();
    write("callscribe.demo.liveCallStarted", started);
    return {
      state: "calling",
      direction: "outgoing",
      phone: null,
      card: null,
      started_at: started,
      ended_at: null,
      duration_s: null,
      updated_at: started,
      lead: { id: lead.id, name: lead.name ?? lead.phone_e164 },
    };
  }

  onLiveCall(): () => void {
    return () => {};
  }

  onTeamActivity(): () => void {
    return () => {};
  }

  async setCallBrief(memberId: string, on: boolean) {
    this.requireManager();
    const target = this.db.members.find((m) => m.id === memberId);
    if (!target) throw new ApiError("No such person.", "P0002");
    target.call_brief = on;
  }

  async setCallProfile(
    memberId: string,
    patch: { call_type?: CallType | null; ai_note?: string; work_name?: string | null },
  ) {
    this.requireAdmin();
    this.requireAi();
    const target = this.db.members.find((m) => m.id === memberId);
    if (!target) throw new ApiError("No such member.", "P0002");
    if (patch.ai_note !== undefined && patch.ai_note.length > MAX_NOTE) {
      throw new ApiError(`Keep the note under ${MAX_NOTE} characters.`, "23514");
    }
    Object.assign(target, patch);
  }

  async checklistSummary(days: number): Promise<ChecklistTally[]> {
    const since = Date.now() - Math.min(Math.max(days, 1), 90) * 86_400_000;
    const tallies = new Map<string, ChecklistTally>();
    // Like the database: managers see the team, a telecaller only their own calls (DASH-05).
    for (const i of this.mine(this.db.insights)) {
      if (i.kind !== "checklist" || !i.member_id || !i.category || new Date(i.created_at).getTime() <= since) continue;
      const key = `${i.member_id}/${i.category}`;
      const t = tallies.get(key) ?? { member_id: i.member_id, item: i.category, done: 0, missed: 0 };
      if (i.result === "yes") t.done++;
      if (i.result === "no") t.missed++;
      tallies.set(key, t);
    }
    return [...tallies.values()];
  }

  async setBusinessType(type: BusinessType): Promise<BusinessTypeResult> {
    this.requireAdmin();
    // The same steps as private.apply_business_type() (ORG-09).
    const templates = {
      real_estate: DEMO_CATEGORIES,
      software: DEMO_SOFTWARE_CATEGORIES,
      education: DEMO_EDUCATION_CATEGORIES,
    };
    const checklists = {
      real_estate: DEMO_CHECKLIST,
      software: DEMO_SOFTWARE_CHECKLIST,
      education: DEMO_EDUCATION_CHECKLIST,
    };
    const previous = asBusinessType(this.db.org.vertical);
    const before = new Map(
      templates[previous].map(([category, label, description]) => [category, { label, description }]),
    );
    let added = 0;
    templates[type].forEach(([category, label, description], i) => {
      const sort = category === "other" ? 99 : i + 1;
      const row = this.db.taxonomies.find((t) => t.category === category);
      if (!row) {
        added++;
        this.db.taxonomies.push({
          id: newId(),
          org_id: this.db.org.id,
          kind: "objection",
          category,
          label,
          description,
          active: true,
          sort,
          created_at: new Date().toISOString(),
        });
        return;
      }
      const old = before.get(category);
      Object.assign(row, {
        active: true,
        sort,
        label: old && row.label === old.label ? label : row.label,
        description: old && row.description === old.description ? description : row.description,
      });
    });
    let hidden = 0;
    for (const row of this.db.taxonomies) {
      const fits = templates[type].some(([category]) => category === row.category);
      if (row.active && row.category !== "other" && before.has(row.category) && !fits) {
        row.active = false;
        hidden++;
      }
    }
    const unchanged = JSON.stringify(this.db.aiInstructions.checklist) === JSON.stringify(checklists[previous]) ||
      this.db.aiInstructions.checklist.length === 0;
    if (unchanged) {
      this.db.aiInstructions = {
        ...this.db.aiInstructions,
        checklist: checklists[type],
        version: this.db.aiInstructions.version + 1,
        updated_at: new Date().toISOString(),
        updated_by: this.me().id,
      };
    }
    this.db.org = { ...this.db.org, vertical: type, updated_at: new Date().toISOString() };
    return {
      vertical: type,
      previous,
      categories_added: added,
      categories_hidden: hidden,
      checklist_replaced: unchanged,
    };
  }

  async usage(): Promise<Usage> {
    this.requireAdmin();
    const used = this.db.usageByDay.reduce((a, b) => a + b.minutes, 0);
    return {
      month_start: this.db.usageByDay[0]?.day ?? localDate(new Date(), DEMO_TZ),
      included: this.db.org.ai_minutes_included,
      used,
      recordings: Math.round(used / 2.4),
      held: 0,
      by_day: this.db.usageByDay,
    };
  }

  async analysisReview(): Promise<ReviewLead[]> {
    return structuredClone(DEMO_REVIEW);
  }

  async analysisFeedback(recordingId: string, isRight: boolean, note?: string) {
    for (const lead of DEMO_REVIEW) {
      for (const call of lead.calls) {
        if (call.recording_id === recordingId) call.feedback = { is_right: isRight, note: note ?? null };
      }
    }
  }

  async digests() {
    this.requireManager();
    return [...this.db.digests];
  }

  // -------------------------------------------------------------- platform

  async platformOrgs() {
    this.requirePlatform();
    return [...this.db.platformOrgs];
  }

  async platformAudit() {
    this.requirePlatform();
    return [...this.db.platformAudit];
  }

  async platformCreateOrg(
    input: {
      name: string;
      ownerName: string;
      ownerEmail?: string;
      ownerPhone?: string;
      vertical: BusinessType;
      addOns: AddOns;
      memberLimit: number | null;
    },
  ) {
    this.requirePlatform();
    if (!input.name.trim()) throw new ApiError("Give the organization a name.", "22023");
    const now = new Date().toISOString();
    this.db.platformOrgs.unshift({
      id: newId(),
      name: input.name,
      status: "active",
      suspended_reason: "",
      timezone: DEMO_TZ,
      default_country: "IN",
      created_at: now,
      owners: [] as unknown as Json,
      active_members: 0,
      callers: 0,
      devices: 0,
      pending_invitations: 1,
      last_activity_at: now,
      ai_minutes_included: 3000,
      ai_minutes_used: 0,
      reviewed_at: now,
      held_recordings: 0,
      features: { ...input.addOns },
      vertical: input.vertical,
      member_limit: input.memberLimit,
      records_calls: input.addOns.coach || input.addOns.autopilot,
    });
    this.audit("organization.created", this.db.platformOrgs[0].id, input.name, {
      owner: input.ownerName,
      vertical: input.vertical,
      coach: input.addOns.coach,
      autopilot: input.addOns.autopilot,
    });
  }

  async platformInviteOwner(input: { orgId: string; ownerName: string }) {
    this.requirePlatform();
    const org = this.db.platformOrgs.find((o) => o.id === input.orgId);
    if (!org) throw new ApiError("No such organization.", "P0002");
    org.pending_invitations += 1;
    this.audit("owner.invited", org.id, org.name, { name: input.ownerName });
  }

  async platformSetStatus(
    orgId: string,
    status: "active" | "suspended",
    reason: string,
  ) {
    this.requirePlatform();
    if (!reason.trim()) {
      throw new ApiError(
        "Give a reason; the organization's owner will see it.",
        "22023",
      );
    }
    const org = this.db.platformOrgs.find((o) => o.id === orgId);
    if (!org) throw new ApiError("No such organization.", "P0002");
    org.status = status;
    org.suspended_reason = status === "suspended" ? reason : "";
    this.audit(`organization.${status}`, org.id, org.name, { reason });
  }

  // Mirrors platform_approve_organization(): once, and the calls saved while under review go to the AI.
  async platformApproveOrg(orgId: string) {
    this.requirePlatform();
    const org = this.db.platformOrgs.find((o) => o.id === orgId);
    if (!org) throw new ApiError("No such organization.", "P0002");
    if (org.reviewed_at) throw new ApiError("This organization is approved already.", "22023");
    const queued = org.held_recordings;
    org.reviewed_at = new Date().toISOString();
    org.held_recordings = 0;
    this.audit("organization.approved", org.id, org.name, { recordings_queued: queued });
    return queued;
  }

  async platformSetMinutes(orgId: string, minutes: number, reason: string) {
    this.requirePlatform();
    if (!reason.trim()) throw new ApiError("Give a reason.", "22023");
    if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1_000_000) {
      throw new ApiError("Minutes must be between 0 and 1,000,000.", "22023");
    }
    const org = this.db.platformOrgs.find((o) => o.id === orgId);
    if (!org) throw new ApiError("No such organization.", "P0002");
    this.audit("organization.ai_minutes", org.id, org.name, {
      from: org.ai_minutes_included,
      to: minutes,
      reason,
    });
    org.ai_minutes_included = minutes;
  }

  // Mirrors platform_set_features(): a reason, and only a change is logged.
  async platformSetAddOns(orgId: string, addOns: AddOns, reason: string) {
    this.requirePlatform();
    if (!reason.trim()) throw new ApiError("Give a reason.", "22023");
    const org = this.db.platformOrgs.find((o) => o.id === orgId);
    if (!org) throw new ApiError("No such organization.", "P0002");
    if (org.features.coach === addOns.coach && org.features.autopilot === addOns.autopilot) return 0;
    this.audit("organization.features", org.id, org.name, {
      from: { ...org.features },
      to: { ...addOns },
      reason,
    });
    org.features = { ...addOns };
    org.records_calls = addOns.coach || addOns.autopilot;
    return 0;
  }

  // Mirrors platform_set_member_limit(): a reason, and never below the people and invitations it has.
  async platformSetMemberLimit(orgId: string, limit: number | null, reason: string) {
    this.requirePlatform();
    if (!reason.trim()) throw new ApiError("Give a reason.", "22023");
    if (limit !== null && (!Number.isInteger(limit) || limit < 1 || limit > 10000)) {
      throw new ApiError("People allowed is 1 to 10,000, or no limit.", "22023");
    }
    const org = this.db.platformOrgs.find((o) => o.id === orgId);
    if (!org) throw new ApiError("No such organization.", "P0002");
    if (org.member_limit === limit) return 0;
    if (limit !== null && org.active_members + org.pending_invitations > limit) {
      throw new ApiError(
        `It has ${org.active_members} people and ${org.pending_invitations} open invitations: remove some first, or allow more.`,
        "22023",
      );
    }
    this.audit("organization.member_limit", org.id, org.name, { from: org.member_limit, to: limit, reason });
    org.member_limit = limit;
    return 0;
  }

  // Mirrors platform_update_organization(): only what changed is logged.
  async platformUpdateOrg(
    orgId: string,
    input: { name: string; vertical: BusinessType; timezone: string; defaultCountry: string },
  ) {
    this.requirePlatform();
    if (!input.name.trim()) throw new ApiError("Give the organization a name.", "22023");
    if (!/^[A-Za-z]{2}$/.test(input.defaultCountry.trim())) {
      throw new ApiError("The country is a two-letter code, such as IN.", "22023");
    }
    const org = this.db.platformOrgs.find((o) => o.id === orgId);
    if (!org) throw new ApiError("No such organization.", "P0002");
    const next = {
      name: input.name.trim(),
      vertical: input.vertical,
      timezone: input.timezone,
      default_country: input.defaultCountry.trim().toUpperCase(),
    };
    const changes: Record<string, Json> = {};
    for (const key of ["name", "vertical", "timezone", "default_country"] as const) {
      if (org[key] !== next[key]) changes[key] = { from: org[key], to: next[key] };
    }
    Object.assign(org, next);
    if (Object.keys(changes).length > 0) this.audit("organization.details", org.id, org.name, changes);
  }

  private audit(
    action: string,
    orgId: string,
    orgName: string,
    detail: Record<string, Json>,
  ) {
    this.db.platformAudit.unshift({
      at: new Date().toISOString(),
      org_id: orgId,
      org_name: orgName,
      actor_email: "admin@callscribe.example",
      action,
      detail,
    });
  }

  // ----------------------------------------------------------------- billing

  private invoices: Invoice[] = demoInvoices(this.db.org.id);

  async billing(): Promise<{ summary: BillingSummary; invoices: Invoice[] }> {
    this.requireAdmin();
    const toppedUp = this.invoices.filter((i) =>
      i.kind === "topup" && i.status === "paid" && i.paid_at! >= monthStart()
    )
      .reduce((a, i) => a + (i.lines as unknown as { minutes?: number }[])[0]?.minutes!, 0);
    const used = this.db.usageByDay.reduce((a, b) => a + b.minutes, 0);
    return {
      summary: {
        plan: { name: "Base", monthly_paise: 299900, included_users: 5, extra_user_paise: 19900 },
        trial_ends_on: this.db.org.created_at.slice(0, 10),
        exempt: false,
        gst_rate: 0.18,
        users: this.db.members.filter((m) => m.status === "active").length,
        minutes: {
          included: this.db.org.ai_minutes_included,
          topped_up: toppedUp,
          remaining: this.db.org.ai_minutes_included + toppedUp - used,
        },
        packs: [{ code: "m1000", minutes: 1000, price_paise: 99900 }, {
          code: "m5000",
          minutes: 5000,
          price_paise: 449900,
        }],
        seller: {},
      },
      invoices: [...this.invoices].sort((a, b) => b.issued_at.localeCompare(a.issued_at)),
    };
  }

  async buyMinutes(pack: string) {
    this.requireAdmin();
    const packs: Record<string, [number, number]> = { m1000: [1000, 99900], m5000: [5000, 449900] };
    if (!packs[pack]) throw new ApiError("No such minute pack.", "22023");
    if (this.invoices.filter((i) => i.kind === "topup" && i.status === "issued").length >= 3) {
      throw new ApiError("Pay the minute packs already ordered first.", "22023");
    }
    const [minutes, price] = packs[pack];
    this.invoices.unshift(demoInvoice(
      this.db.org.id,
      this.invoices.length + 1,
      "topup",
      null,
      [
        {
          description: `${minutes.toLocaleString("en-IN")} AI minutes`,
          quantity: 1,
          unit_paise: price,
          amount_paise: price,
          minutes,
        },
      ],
      "issued",
      new Date(),
    ));
  }

  async platformHealth(): Promise<PlatformHealth> {
    this.requirePlatform();
    const now = new Date();
    return {
      ai_mode: this.aiMode,
      queued: 3,
      processing: 1,
      held: 0,
      failed_24h: 1,
      done_24h: 164,
      oldest_queued_at: new Date(now.getTime() - 4 * 60_000).toISOString(),
      alerts: [{
        key: "invoice_overdue",
        org: "Metro Brokers",
        severity: "warning",
        message: "Metro Brokers has 1 unpaid invoice(s), the oldest due 07 Sep.",
        first_seen: new Date(now.getTime() - 16 * 86_400_000).toISOString(),
        last_seen: now.toISOString(),
      }],
    };
  }

  private aiMode: "live" | "paused" = "live";

  async platformSetAiMode(mode: "live" | "paused", reason: string) {
    this.requirePlatform();
    if (reason.trim().length < 3) throw new ApiError("Give a reason.", "22023");
    this.aiMode = mode;
  }

  async platformAiUsage(from: string, to: string, limit = 500): Promise<PlatformAiUsage> {
    this.requirePlatform();
    // Made-up metering for the demo company's calls, at about the token rates real calls show.
    const start = new Date(from).getTime();
    const end = new Date(to).getTime();
    const calls: CallUsage[] = this.db.recordings
      .filter((r) => r.processed_at && new Date(r.processed_at).getTime() >= start)
      .filter((r) => new Date(r.processed_at!).getTime() < end)
      .sort((a, b) => b.processed_at!.localeCompare(a.processed_at!))
      .map((r) => {
        const seconds = r.duration_s ?? 60;
        const chars = Math.round(seconds * 9);
        const tIn = Math.round(seconds * 25);
        const tOut = Math.round(chars / 2);
        const aIn = 1000 + Math.round(chars / 1.5);
        const aOut = 450 + Math.round(seconds);
        const tCost = (tIn * 2 + tOut * 12) / 1_000_000;
        const aCost = (aIn * 0.3 + aOut * 2.5) / 1_000_000;
        return {
          // Demo ids share their first digits; the real record uses the first eight characters of a random id.
          ref: r.id.slice(-8),
          org_name: this.db.org.name,
          processed_at: r.processed_at!,
          audio_seconds: seconds,
          billable_minutes: seconds < 10 ? 0 : Math.ceil(seconds / 60),
          transcript_chars: chars,
          transcription: {
            model: "gemini-3.5-transcribe",
            input_tokens: tIn,
            output_tokens: tOut,
            cost_usd: tCost,
            raw: true,
          },
          analysis: {
            model: "gemini-3.5-flash-lite",
            input_tokens: aIn,
            output_tokens: aOut,
            cost_usd: aCost,
            raw: true,
          },
          cost_usd: tCost + aCost,
        };
      });
    const sum = (f: (c: CallUsage) => number) => calls.reduce((s, c) => s + f(c), 0);
    const step = (key: "transcription" | "analysis"): ModelUsage => ({
      step: key,
      model: calls[0]?.[key].model ?? "",
      calls: calls.length,
      input_tokens: sum((c) => c[key].input_tokens ?? 0),
      output_tokens: sum((c) => c[key].output_tokens ?? 0),
      cost_usd: sum((c) => c[key].cost_usd ?? 0),
      without_output: 0,
    });
    const totals = {
      calls: calls.length,
      audio_seconds: sum((c) => c.audio_seconds ?? 0),
      billable_minutes: sum((c) => c.billable_minutes ?? 0),
      transcript_chars: sum((c) => c.transcript_chars ?? 0),
      transcription_input_tokens: sum((c) => c.transcription.input_tokens ?? 0),
      transcription_output_tokens: sum((c) => c.transcription.output_tokens ?? 0),
      transcription_cost_usd: sum((c) => c.transcription.cost_usd ?? 0),
      transcriptions_without_output: 0,
      transcript_chars_without_output: 0,
      analysis_input_tokens: sum((c) => c.analysis.input_tokens ?? 0),
      analysis_output_tokens: sum((c) => c.analysis.output_tokens ?? 0),
      analysis_cost_usd: sum((c) => c.analysis.cost_usd ?? 0),
      cost_usd: sum((c) => c.cost_usd),
    };
    return {
      from,
      to,
      totals,
      by_model: calls.length ? [step("transcription"), step("analysis")] : [],
      by_org: calls.length
        ? [{
          org_id: this.db.org.id,
          org_name: this.db.org.name,
          calls: totals.calls,
          audio_seconds: totals.audio_seconds,
          billable_minutes: totals.billable_minutes,
          transcript_chars: totals.transcript_chars,
          input_tokens: totals.transcription_input_tokens + totals.analysis_input_tokens,
          output_tokens: totals.transcription_output_tokens + totals.analysis_output_tokens,
          cost_usd: totals.cost_usd,
        }]
        : [],
      calls: calls.slice(0, limit),
    };
  }

  /** What the platform knows of each company beyond its settings (D-182): Voxowa's own, a quiet spell, a note. */
  private companyFlags = new Map<string, { internal: boolean; quiet_until: string | null; note: string | null }>();

  /** A made-up health for a company in the console: the demo company busy but slipping on follow-ups, the newer ones
   * onboarding, the suspended one suspended. Counts only, as on the server. */
  private demoCompanyHealth(o: PlatformOrg): CompanyHealth {
    const days = Math.floor((Date.now() - new Date(o.created_at).getTime()) / 86_400_000);
    const flags = this.companyFlags.get(o.id) ?? { internal: false, quiet_until: null, note: null };
    const today = localDate(new Date(), DEMO_TZ);
    const ours = o.id === this.db.org.id;
    const active = o.status === "active";
    const status: CompanyStatus = !active ? "suspended" : days < 14 ? "onboarding" : ours ? "watch" : "healthy";
    const owners = (o.owners as unknown[] | null) ?? [];
    const busy = ours ? 1 : active && days >= 14 ? 0.3 : 0;
    const calls7 = !active ? 0 : days < 14 ? Math.min(days, 7) * 18 : Math.round(574 * busy);
    const done7 = Math.round(96 * busy) + (active && days >= 7 && days < 14 ? 6 : 0);
    const overdue = ours ? 128 : active ? Math.round(9 * busy) : 0;
    const connected = Math.round(calls7 * 0.52);
    const outcomes = ours ? Math.round(connected * 0.41) : Math.round(connected * 0.8);
    const reasons = status === "watch"
      ? [
        `${overdue} follow-ups overdue, more than the ${done7} done this week`,
        `What happened is noted on only ${Math.round((100 * outcomes) / connected)}% of connected calls`,
      ]
      : [];
    return {
      org_id: o.id,
      name: o.name,
      org_status: active ? "active" : "suspended",
      status,
      internal: flags.internal,
      quiet: flags.quiet_until !== null && flags.quiet_until >= today,
      quiet_until: flags.quiet_until,
      note: flags.note,
      reasons,
      checklist: [
        { key: "owner", label: "Owner signed in", done: owners.length > 0 },
        { key: "team", label: "Team joined", done: o.active_members >= Math.min(o.member_limit ?? 2, 2) },
        { key: "phone", label: "A phone connected", done: o.devices > 0 },
        { key: "calls", label: "First calls synced", done: days >= 2 },
        { key: "follow_up", label: "First follow-up done", done: days >= 7 },
        { key: "customers", label: "Customers added or a lead source connected", done: ours || days >= 7 },
      ],
      integrations: this.demoIntegrations(o, days),
      requested: ours
        ? [{ key: "zoho-crm", use_case: "both", at: new Date(Date.now() - 5 * 86_400_000).toISOString() }]
        : days < 14 && active
        ? [{ key: "indiamart", use_case: "leads_in", at: new Date().toISOString() }]
        : [],
      features: o.features,
      member_limit: o.member_limit,
      vertical: o.vertical,
      created_at: o.created_at,
      days_live: days,
      metrics: {
        calls_7: calls7,
        calls_usual: days >= 14 ? Math.round(612 * (busy || 0.25)) : null,
        connected_7: connected,
        active_7: active ? o.callers : 0,
        active_usual: days >= 14 ? o.callers : null,
        members: o.active_members,
        callers: o.callers,
        done_7: done7,
        on_time_7: Math.round(done7 * 0.74),
        overdue,
        connected_calls_7: connected,
        outcomes_7: outcomes,
        crm_actions_7: Math.round(212 * busy),
        leads_7: Math.round(34 * busy) + (active && days < 14 ? 5 : 0),
        phones: o.devices,
        phones_synced_24h: active ? o.devices : 0,
        last_call_day: active && days >= 2 ? today : null,
        silent_working_days: 0,
        invitations_waiting: o.pending_invitations,
        manager_seen_at: active ? new Date(Date.now() - 2 * 3_600_000).toISOString() : null,
        manager_days_away: active ? 0 : 21,
      },
    };
  }

  /** Made-up integrations: the demo company's Facebook, MyOperator and a JustDial address with no lead yet; a website
   * form at the newer company; JustDial turned off at the suspended one. */
  private demoIntegrations(o: PlatformOrg, days: number): CompanyIntegration[] {
    const ago = (d: number) => new Date(Date.now() - d * 86_400_000).toISOString();
    const one = (key: string, status: CompanyIntegration["status"], since: number, week: number, all: number) => ({
      key,
      status,
      connected_at: ago(since),
      last_sync_at: status === "connected" ? ago(0.05) : null,
      error: null,
      received_7: week,
      received: all,
      unread_7: week > 10 ? 2 : 0,
      last_at: all > 0 ? ago(week > 0 ? 0.1 : 9) : null,
      pages: key === "facebook-lead-ads" ? 1 : 0,
    });
    if (o.id === this.db.org.id) {
      return [
        one("facebook-lead-ads", "connected", 30, 34, 212),
        one("myoperator", "connected", 21, 88, 640),
        one("justdial", "waiting", 6, 0, 0),
      ];
    }
    if (o.status !== "active") return [one("justdial", "disconnected", 50, 0, 41)];
    return days >= 7 ? [one("website-webhook", "connected", days - 2, 6, 9)] : [];
  }

  private demoCompany(orgId: string): PlatformOrg {
    const org = this.db.platformOrgs.find((o) => o.id === orgId);
    if (!org) throw new ApiError("No such organization.", "P0002");
    return org;
  }

  async platformHealthOverview(): Promise<HealthOverview> {
    this.requirePlatform();
    const companies = this.db.platformOrgs.map((o) => this.demoCompanyHealth(o));
    const counted = companies.filter((c) => !c.internal && c.org_status === "active");
    const sum = (f: (c: CompanyHealth) => number) => counted.reduce((a, c) => a + f(c), 0);
    return {
      companies,
      week: {
        calls_7: sum((c) => c.metrics.calls_7),
        calls_prev_7: sum((c) => c.metrics.calls_usual ?? c.metrics.calls_7),
        people_active_7: sum((c) => c.metrics.active_7),
        done_7: sum((c) => c.metrics.done_7),
        on_time_7: sum((c) => c.metrics.on_time_7),
      },
    };
  }

  async platformCompanyHealth(orgId: string): Promise<CompanyHealthDetail> {
    this.requirePlatform();
    const health = this.demoCompanyHealth(this.demoCompany(orgId));
    const live = Math.min(28, health.days_live + 1);
    const perDay = (health.metrics.calls_usual ?? health.metrics.calls_7) / 6;
    return {
      ...health,
      days: Array.from({ length: 28 }, (_, i) => {
        const at = new Date(Date.now() - (27 - i) * 86_400_000);
        const day = localDate(at, DEMO_TZ);
        const working = new Date(`${day}T00:00:00Z`).getUTCDay() !== 0;
        const started = i >= 28 - live && health.org_status === "active";
        return {
          day,
          calls: working && started ? Math.round(perDay * (0.8 + ((i * 7) % 5) / 10)) : 0,
          overdue: i >= 25 ? health.metrics.overdue - (27 - i) * 4 : null,
          working,
        };
      }),
    };
  }

  async platformCompanyTeam(orgId: string): Promise<CompanyTeamMember[]> {
    this.requirePlatform();
    const org = this.demoCompany(orgId);
    this.audit("organization.team_viewed", org.id, org.name, {});
    const hour = (n: number) => new Date(Date.now() - n * 3_600_000).toISOString();
    const phone = (model: string, appVersion: string, synced: number, permissions: Record<string, boolean>) => ({
      model,
      android: "14",
      app_version: appVersion,
      last_sync_at: hour(synced),
      last_seen_at: hour(synced),
      sync_problem: null,
      files_waiting: 0,
      permissions,
      permissions_at: hour(synced),
    });
    const allowed = { call_log: true, notifications: true, recordings: true, after_call: true, battery: true };
    if (org.id === this.db.org.id) {
      return this.db.members.filter((m) => m.status === "active").map((m, i) => {
        const device = this.db.devices.find((d) => d.member_id === m.id && d.revoked_at === null);
        const caller = m.role === "caller";
        return {
          member_id: m.id,
          name: m.display_name,
          role: m.role,
          phone_last4: m.phone_e164 ? m.phone_e164.slice(-4) : null,
          since: m.created_at.slice(0, 10),
          phone: device
            ? phone(
              [device.manufacturer, device.model].filter(Boolean).join(" ") || "Android phone",
              device.app_version ?? "1.4.0",
              i % 3,
              i === 2 ? { ...allowed, notifications: false } : allowed,
            )
            : null,
          uses: device ? (caller ? "phone" : "both") : "dashboard",
          dashboard_at: caller ? null : hour(2),
          calls_7: caller ? 110 + 9 * i : 0,
          calls_usual: caller ? 120 + 7 * i : null,
          done_7: caller ? 18 + i : 0,
          overdue: caller ? 20 + 3 * i : 0,
        };
      });
    }
    const owners = (org.owners as unknown as { name: string; phone: string | null }[] | null) ?? [];
    const names = ["Anita Rao", "Kiran Shetty", "Pooja Nair", "Deepak Joshi"];
    return [
      ...owners.map((w, i) => ({
        member_id: `${org.id}-owner-${i}`,
        name: w.name,
        role: "owner" as const,
        phone_last4: w.phone ? w.phone.slice(-4) : null,
        since: org.created_at.slice(0, 10),
        phone: null,
        uses: org.status === "active" ? "dashboard" as const : "neither" as const,
        dashboard_at: org.status === "active" ? hour(5) : null,
        calls_7: 0,
        calls_usual: null,
        done_7: 0,
        overdue: 0,
      })),
      ...names.slice(0, org.callers).map((name, i) => ({
        member_id: `${org.id}-caller-${i}`,
        name,
        role: "caller" as const,
        phone_last4: String(4101 + 37 * i),
        since: org.created_at.slice(0, 10),
        phone: phone(i % 2 ? "Redmi Note 13" : "Samsung Galaxy M34", i % 2 ? "1.3.2" : "1.4.0", 1 + i, {
          ...allowed,
          battery: i % 2 === 0,
        }),
        uses: org.status === "active" ? "phone" as const : "neither" as const,
        dashboard_at: null,
        calls_7: org.status === "active" ? 40 + 6 * i : 0,
        calls_usual: null,
        done_7: org.status === "active" ? 3 : 0,
        overdue: 1,
      })),
    ];
  }

  async platformSetCompanyFlags(
    orgId: string,
    flags: { internal: boolean; quietUntil: string | null; note: string | null },
  ): Promise<CompanyHealth> {
    this.requirePlatform();
    const org = this.demoCompany(orgId);
    const note = flags.note?.trim() || null;
    if (note && note.length > 300) throw new ApiError("Keep the note under 300 characters.", "22023");
    this.companyFlags.set(org.id, { internal: flags.internal, quiet_until: flags.quietUntil, note });
    this.audit("organization.flags", org.id, org.name, { internal: flags.internal, quiet_until: flags.quietUntil });
    return this.demoCompanyHealth(org);
  }

  async platformIntegrationDemand(): Promise<IntegrationDemand[]> {
    this.requirePlatform();
    const day = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString();
    const row = (
      key: string,
      orgs: number,
      seats: number,
      paying: number,
      paidSeats: number,
      cases: [number, number, number, number],
      opens: number,
      notes: { org: string; note: string; at: string }[] = [],
    ): IntegrationDemand => ({
      key,
      orgs_requesting: orgs,
      seats,
      paying_orgs: paying,
      paid_seats: paidSeats,
      use_cases: { leads_in: cases[0], calls_out: cases[1], both: cases[2], other: cases[3] },
      markets: ["IN"],
      notes,
      first_requested_at: orgs ? day(20) : null,
      last_requested_at: orgs ? day(1) : null,
      notified: 0,
      orgs_opened: Math.max(orgs, Math.ceil(opens / 3)),
      opens,
      last_opened_at: day(0),
    });
    return [
      row("myoperator", 4, 31, 3, 24, [0, 3, 1, 0], 19, [
        { org: "Sharma Realty", note: "Our IVR runs on MyOperator.", at: day(1) },
      ]),
      row("indiamart", 3, 18, 2, 12, [3, 0, 0, 0], 11, [
        { org: "Sharma Realty", note: "Most of our enquiries come from IndiaMART.", at: day(2) },
      ]),
      row("facebook-lead-ads", 3, 14, 1, 6, [2, 0, 1, 0], 23),
      row("zoho-crm", 1, 8, 1, 8, [0, 0, 1, 0], 6, [
        { org: "Kapoor Motors", note: "Sales team lives in Zoho.", at: day(5) },
      ]),
      row("google-sheets", 2, 9, 0, 0, [1, 0, 0, 1], 9),
      row("mcp-claude", 0, 0, 0, 0, [0, 0, 0, 0], 4),
    ];
  }

  async platformInvoices(status?: "issued" | "paid" | "void"): Promise<PlatformInvoice[]> {
    this.requirePlatform();
    const names = new Map(this.db.platformOrgs.map((o) => [o.id, o.name]));
    const metro = this.db.platformOrgs[2];
    const all = [
      ...this.invoices,
      demoInvoice(
        metro.id,
        90,
        "subscription",
        "2026-08-01",
        [
          { description: "Base plan, August 2026", quantity: 1, unit_paise: 299900, amount_paise: 299900 },
          { description: "Extra users above 5", quantity: 2, unit_paise: 19900, amount_paise: 39800 },
        ],
        "issued",
        new Date(Date.now() - 23 * 86_400_000),
      ),
    ];
    return all.filter((i) => !status || i.status === status).map((i) => ({
      id: i.id,
      org_id: i.org_id,
      org_name: names.get(i.org_id) ?? "",
      number: i.number,
      kind: i.kind,
      period_start: i.period_start ?? "",
      total_paise: i.total_paise,
      status: i.status,
      issued_at: i.issued_at,
      due_at: i.due_at,
      paid_at: i.paid_at ?? "",
      payment_ref: i.payment_ref ?? "",
    }));
  }

  async platformMarkPaid(invoiceId: string, reference: string) {
    this.requirePlatform();
    if (!reference.trim()) throw new ApiError("Give the payment reference (UTR or UPI id).", "22023");
    const inv = this.invoices.find((i) => i.id === invoiceId);
    if (!inv || inv.status !== "issued") throw new ApiError("That invoice is not waiting for payment.", "22023");
    inv.status = "paid";
    inv.paid_at = new Date().toISOString();
    inv.payment_ref = reference.trim();
  }

  async platformVoidInvoice(invoiceId: string, reason: string) {
    this.requirePlatform();
    if (!reason.trim()) throw new ApiError("Give a reason.", "22023");
    const inv = this.invoices.find((i) => i.id === invoiceId);
    if (!inv || inv.status !== "issued") throw new ApiError("Only an unpaid invoice can be voided.", "22023");
    inv.status = "void";
    inv.void_reason = reason.trim();
  }
}

function monthStart(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString();
}

/** The analysis review page, with one made-up lead. */
const DEMO_REVIEW: ReviewLead[] = [{
  lead_id: "demo-review-lead",
  name: "Rohit Verma",
  state: {
    summary:
      "Civil final-year student in Gomti Nagar wants AutoCAD. Agreed to come to the centre on Sunday with his father.",
    interest: "hot",
    course: "autocad",
    planned: "centre_visit",
    pay: 3,
    place: "Gomti Nagar",
    background: "B.Tech civil final year",
    objections: [{ c: "timing", answered: false }],
    next: { visit: true, reschedule: false },
  },
  calls: [{
    recording_id: "demo-review-call-1",
    started_at: "2026-11-01T10:00:00Z",
    direction: "outgoing",
    duration_s: 92,
    caller: "Priya",
    status: "done",
    pass: "full",
    skip_reason: null,
    feedback: null,
    transcript:
      "[0] hello sir main Priya CADD Mantra se\n[3] haan boliye exam khatam ho gaye\n[10] Sunday gyarah baje aa jaunga papa ke saath",
    record: {
      reached: "person",
      summary: "The customer agreed to visit the centre on Sunday at 11 with his father.",
      interest_after: "hot",
      values: { interest: { v: "hot", q: "Sunday gyarah baje aa jaunga" } },
      next: {
        any: true,
        visit: { r: true, q: "Sunday gyarah baje aa jaunga" },
        send: { r: false },
        demo: { r: false },
        callback: { r: false },
      },
      checklist: [{ k: "introduced", r: "yes" }, { k: "asked_goal", r: "no" }, { k: "proposed_next_step", r: "yes" }, {
        k: "asked_to_enrol",
        r: "na",
      }],
    },
  }],
}];

const DEMO_FACEBOOK_PAGES: Omit<FacebookPageChoice, "connected">[] = [
  { id: "100000000000001", name: "Green Meadows Realty", may_read_leads: true },
  { id: "100000000000002", name: "Green Meadows Plots", may_read_leads: true },
  { id: "100000000000003", name: "Rahul's Photography", may_read_leads: false },
];

const DEMO_FACEBOOK_FORMS: FacebookPage["forms"] = [
  { id: "7000000000001", name: "Site visit enquiry", status: "ACTIVE", assign: null, new: false },
  { id: "7000000000002", name: "Brochure request", status: "ACTIVE", assign: null, new: false },
  { id: "7000000000003", name: "Diwali offer 2025", status: "ARCHIVED", assign: null, new: false },
  { id: "7000000000004", name: "Weekend open house", status: "ACTIVE", assign: null, new: true },
];

function emptyFacebook(): FacebookState {
  return {
    status: "disconnected",
    connected_at: null,
    last_sync_at: null,
    last_error: null,
    facebook_user: null,
    login_expires_at: null,
    assign: { mode: "none", members: [] },
    import: null,
    calls_since: null,
    pages: [],
    leads: [],
    counts: { waiting: 0, today: 0, week: 0, skipped_week: 0, failed_week: 0 },
  };
}

function emptyWebhook(): WebhookState {
  return {
    status: "disconnected",
    connected_at: null,
    last_lead_at: null,
    address: null,
    assign: { mode: "none", members: [] },
    fields: {},
    seen_fields: [],
    leads: [],
    counts: { today: 0, week: 0, unreadable: 0, waiting: 0, skipped_week: 0 },
  };
}

/** Only an integration that takes leads by webhook has an address (D-158). */
function webhookKey(key: string) {
  if (!INTEGRATIONS.some((i) => i.key === key && i.webhook)) throw new ApiError("Unknown integration.", "22023");
}

/** An address as Voxowa makes one, with its own secret; the demo's goes nowhere. */
function demoWebhookAddress(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  const secret = btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_");
  return `https://example.supabase.co/functions/v1/lead-webhook/${secret}`;
}

/** IndiaMART's leads as its push sends them (D-176): two pushed, and a third the daily read brings in. */
function demoIndiaMartLeads(): [string, string][][] {
  const at = (minutes: number) =>
    new Date(Date.now() + 330 * 60_000 - minutes * 60_000).toISOString().slice(0, 19).replace("T", " ");
  const lead = (
    id: string,
    type: string,
    minutes: number,
    name: string,
    mobile: string,
    city: string,
    product: string,
  ) =>
    [
      ["CODE", "200"],
      ["STATUS", "SUCCESS"],
      ["RESPONSE.UNIQUE_QUERY_ID", id],
      ["RESPONSE.QUERY_TYPE", type],
      ["RESPONSE.QUERY_TIME", at(minutes)],
      ["RESPONSE.SENDER_NAME", name],
      ["RESPONSE.SENDER_MOBILE", mobile],
      ["RESPONSE.SENDER_CITY", city],
      ["RESPONSE.QUERY_PRODUCT_NAME", product],
      ["RESPONSE.QUERY_MESSAGE", `I want to know about ${product}.`],
    ] as [string, string][];
  return [
    lead("2900100001", "W", 3, "Sanjay Mehta", "+91-9876509201", "Pune", "AutoCAD Course"),
    lead("2900100002", "P", 1, "Farah Khan", "+91-9876509202", "Mumbai", "Revit Architecture Training"),
    lead("2900100003", "B", 200, "Deepak Nair", "+91-9876509203", "Kochi", "SolidWorks Course"),
  ];
}

/** Three leads as a JustDial listing sends them; the third names its number in a way Voxowa does not know. */
function demoWebhookLeads(): [string, string][][] {
  const today = new Date().toISOString().slice(0, 10);
  return [
    [
      ["leadid", "JD100245"],
      ["leadtype", "category"],
      ["prefix", "Mr"],
      ["name", "Kunal Bhatia"],
      [
        "mobile",
        "9876509101",
      ],
      ["date", today],
      ["category", "Interior Designing Institutes"],
      ["city", "Delhi"],
      ["area", "Pitampura"],
    ],
    [
      ["leadid", "JD100246"],
      ["leadtype", "company"],
      ["prefix", "Ms"],
      ["name", "Ritika Arora"],
      [
        "mobile",
        "9876509102",
      ],
      ["date", today],
      ["category", "AutoCAD Training Institutes"],
      ["city", "Delhi"],
      ["area", "Rohini"],
    ],
    [["leadid", "JD100247"], ["Customer", "Mohit Sehgal"], ["Contact Mobile", "9876509103"], ["date", today], [
      "category",
      "Revit Training",
    ], ["city", "Delhi"]],
  ];
}

function demoInvoice(
  orgId: string,
  n: number,
  kind: "subscription" | "topup",
  periodStart: string | null,
  lines: { description: string; quantity: number; unit_paise: number; amount_paise: number; minutes?: number }[],
  status: "issued" | "paid",
  issued: Date,
): Invoice {
  const subtotal = lines.reduce((a, l) => a + l.amount_paise, 0);
  const gst = Math.round(subtotal * 0.18);
  const periodEnd = periodStart
    ? new Date(Date.UTC(+periodStart.slice(0, 4), +periodStart.slice(5, 7), 0)).toISOString().slice(0, 10)
    : null;
  return {
    id: `20000000-0000-4000-8000-${String(n).padStart(12, "0")}`,
    org_id: orgId,
    number: `CS/26-27/${String(n).padStart(6, "0")}`,
    kind,
    period_start: periodStart,
    period_end: periodEnd,
    lines: lines as unknown as Json,
    subtotal_paise: subtotal,
    gst_paise: gst,
    total_paise: subtotal + gst,
    status,
    issued_at: issued.toISOString(),
    due_at: new Date(issued.getTime() + (kind === "topup" ? 0 : 7) * 86_400_000).toISOString(),
    paid_at: status === "paid" ? new Date(issued.getTime() + 2 * 86_400_000).toISOString() : null,
    payment_ref: status === "paid" ? `UTR${412000000 + n}` : null,
    void_reason: null,
    created_at: issued.toISOString(),
  };
}

function demoInvoices(orgId: string): Invoice[] {
  const plan = (month: string) => [
    { description: `Base plan, ${month}`, quantity: 1, unit_paise: 299900, amount_paise: 299900 },
    { description: "Extra users above 5", quantity: 1, unit_paise: 19900, amount_paise: 19900 },
  ];
  return [
    demoInvoice(orgId, 41, "subscription", "2026-08-01", plan("August 2026"), "paid", new Date("2026-09-01T03:15:00Z")),
    demoInvoice(
      orgId,
      57,
      "topup",
      null,
      [
        { description: "1,000 AI minutes", quantity: 1, unit_paise: 99900, amount_paise: 99900, minutes: 1000 },
      ],
      "paid",
      new Date("2026-09-24T06:00:00Z"),
    ),
  ];
}
